import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import axios from "axios";
import { useBackButton } from "../hooks/useBackButton";
import { API, useAuth, useSettings } from "@/App";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import {
  UserCircle,
  Plus,
  Search,
  X,
  Edit,
  Save,
  Upload,
  Calendar,
  MapPin,
  FileText,
  Download,
  Copy,
  Users,
  Activity,
  Link as LinkIcon,
  ExternalLink,
  Filter,
  Bell,
  Play,
  Trash2,
  RefreshCw,
  LayoutGrid,
  List,
  MapPinned,
  AlertTriangle,
  Eye,
  ChevronDown
} from "lucide-react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import ImageLightbox from "@/components/ImageLightbox";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import markerRetina from 'leaflet/dist/images/marker-icon-2x.png';

// Fix default marker icon
const defaultMarkerIcon = L.icon({
  iconUrl: markerIcon,
  iconRetinaUrl: markerRetina,
  shadowUrl: markerShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

function FitBoundsToMarkers({ locations }) {
  const map = useMap();
  useEffect(() => {
    if (locations.length > 0) {
      const bounds = L.latLngBounds(locations.map(l => [l.lat, l.lng]));
      map.fitBounds(bounds, { padding: [40, 40], maxZoom: 16 });
    }
  }, [locations, map]);
  return null;
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Authenticated thumbnail URL — appends session token for cross-domain img tags
const getThumbUrl = (url, size = 80) => {
  if (!url) return null;
  const sessionToken = localStorage.getItem('session_token');
  const base = `${BACKEND_URL}${url}/thumb?size=${size}`;
  return sessionToken ? `${base}&token=${sessionToken}` : base;
};

const getFileThumbUrl = (fileId, size = 200) => {
  if (!fileId) return null;
  const sessionToken = localStorage.getItem('session_token');
  const base = `${BACKEND_URL}/api/uploads/${fileId}/thumb?size=${size}`;
  return sessionToken ? `${base}&token=${sessionToken}` : base;
};

const STATUS_OPTIONS = [
  { value: "none", label: "No Label", color: "bg-slate-500" },
  { value: "linked", label: "Linked", color: "bg-blue-500" },
  { value: "warrant", label: "Warrant", color: "bg-red-500" },
  { value: "ready_for_arrest", label: "Ready for Arrest", color: "bg-amber-500" },
  { value: "arrested", label: "Arrested", color: "bg-emerald-500" }
];

const SEARCH_FIELDS = [
  { value: "all", label: "All Fields" },
  { value: "name", label: "Name" },
  { value: "address", label: "Address" },
  { value: "affiliates", label: "Affiliates" },
  { value: "notes", label: "Notes" },
  { value: "incident", label: "Incident ID" }
];

function formatDob(dob) {
  if (!dob) return "";
  try {
    const [y, m, d] = dob.split("-");
    const date = new Date(Number(y), Number(m) - 1, Number(d));
    return date.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });
  } catch { return dob; }
}

export default function ProfilesAdmin() {
  const { user } = useAuth();
  const { isDark } = useSettings() || {};
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { profileId: pathProfileId } = useParams();

  // Check if user has admin privileges
  const isAdmin = user?.role === 'admin' || user?.role === 'profiles_admin' || user?.perpetrator_admin_access === true;
  const hasIncidentsAccess = user?.role === "admin" || user?.incidents_access;

  // Quick Export of selected incidents → plain-text clipboard summary.
  // Gated on hasIncidentsAccess: dispatch-only roles without the Incidents
  // permission shouldn't be able to scrape callback / victim / witness
  // phone numbers out via this UI.
  const [showQuickExport, setShowQuickExport] = useState(false);
  const [quickExportSelectedIds, setQuickExportSelectedIds] = useState(new Set());
  const [quickExportBusy, setQuickExportBusy] = useState(false);
  const [quickExportPreview, setQuickExportPreview] = useState("");
  const [pendingIncidentNav, setPendingIncidentNav] = useState(null);
  
  const [profiles, setProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [incidentTypes, setIncidentTypes] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  
  // Filters - use separate input state for debouncing
  const [searchInput, setSearchInput] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchField, setSearchField] = useState("all");
  const [filterStatus, setFilterStatus] = useState("ready_for_arrest");
  const [filterHasArrest, setFilterHasArrest] = useState("all");
  const [filterHasReady, setFilterHasReady] = useState("all");
  const [filterHasLinked, setFilterHasLinked] = useState("all");
  const [filterArrestVia, setFilterArrestVia] = useState("all");
  const [filterIncidentType, setFilterIncidentType] = useState("all");
  const [incidentTypeSearch, setIncidentTypeSearch] = useState("");
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [profileSort, setProfileSort] = useState("newest");
  
  // Debounce ref
  const searchTimeoutRef = useRef(null);
  const initialLoadDone = useRef(false);
  
  // Dialogs
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  
  // Forms
  const [newProfile, setNewProfile] = useState({ name: "", mugshot_url: "", known_address: "", affiliates: "", notes: "" });
  const [editProfile, setEditProfile] = useState({});
  const [uploading, setUploading] = useState(false);
  
  // Detail tab
  const [detailTab, setDetailTab] = useState("linked");
  
  // Lightbox state
  const [lightboxImage, setLightboxImage] = useState(null);
  const [lightboxMediaType, setLightboxMediaType] = useState(null);
  const [lightboxHideLogo, setLightboxHideLogo] = useState(false);
  
  // Activity tab
  const [activeTab, setActiveTab] = useState("profiles");
  const [activityDateFrom, setActivityDateFrom] = useState("");
  const [activityDateTo, setActivityDateTo] = useState("");

  // Board tab
  const [boardView, setBoardView] = useState("card"); // card, list, map
  const [boardGridCols, setBoardGridCols] = useState(() => window.innerWidth >= 1024 ? 3 : window.innerWidth >= 640 ? 2 : 1);
  // Board reuses the Profiles-tab filter state (filterStatus, filterIncidentType,
  // filterArrestVia, filterDateFrom, filterDateTo) so toggling a filter on
  // either tab carries over when the user switches views.
  // Multi-select filter — narrow the board (especially the map) to one or
  // more specific profiles. Empty array == no profile filter (all visible).
  const [boardProfileFilter, setBoardProfileFilter] = useState([]);
  const [boardProfileFilterSearch, setBoardProfileFilterSearch] = useState("");
  const [boardProfileFilterOpen, setBoardProfileFilterOpen] = useState(false);
  const [mapLocations, setMapLocations] = useState([]);
  const [mapLoading, setMapLoading] = useState(false);
  const [boardFiltersOpen, setBoardFiltersOpen] = useState(false);
  const [boardPreviewProfile, setBoardPreviewProfile] = useState(null);
  const [boardPreviewLoading, setBoardPreviewLoading] = useState(false);

  const openBoardPreview = async (profileId) => {
    setBoardPreviewLoading(true);
    try {
      const response = await axios.get(`${API}/perpetrator-profiles/${profileId}`, { withCredentials: true });
      setBoardPreviewProfile(response.data);
    } catch {
      toast.error("Failed to load profile");
    }
    setBoardPreviewLoading(false);
  };

  // Profile media
  const [profileMedia, setProfileMedia] = useState([]);
  const idLicenseInputRef = useRef(null);
  const mugshotInputRef = useRef(null);
  const mediaUploadRef = useRef(null);

  // Delete confirmation dialogs
  const [showDeleteMugshotDialog, setShowDeleteMugshotDialog] = useState(false);
  const [showDeleteIdLicenseDialog, setShowDeleteIdLicenseDialog] = useState(false);

  // Search/Sort for detail tabs
  const [linkedSearch, setLinkedSearch] = useState("");
  const [linkedSort, setLinkedSort] = useState("newest");
  const [readySearch, setReadySearch] = useState("");
  const [readySort, setReadySort] = useState("newest");
  const [readyFilterArrested, setReadyFilterArrested] = useState("all"); // all, yes, no
  const [readyFilterReport, setReadyFilterReport] = useState("all"); // all, yes, no
  const [readyFilterType, setReadyFilterType] = useState("all");
  const [readyFilterIdMethod, setReadyFilterIdMethod] = useState("all");
  const [arrestsSearch, setArrestsSearch] = useState("");
  const [arrestsSort, setArrestsSort] = useState("newest");

  // Notification dialog state
  const [showNotifDialog, setShowNotifDialog] = useState(false);
  const [notifTab, setNotifTab] = useState("linked");
  const [notifLinkedSearch, setNotifLinkedSearch] = useState("");
  const [notifLinkedSort, setNotifLinkedSort] = useState("newest");
  const [notifReadySearch, setNotifReadySearch] = useState("");
  const [notifReadySort, setNotifReadySort] = useState("newest");
  const [notifReadyFilterArrested, setNotifReadyFilterArrested] = useState("all");
  const [notifReadyFilterReport, setNotifReadyFilterReport] = useState("all");
  const [notifReadyFilterType, setNotifReadyFilterType] = useState("all");
  const [notifReadyFilterIdMethod, setNotifReadyFilterIdMethod] = useState("all");
  const [notifArrestsSearch, setNotifArrestsSearch] = useState("");
  const [notifArrestsSort, setNotifArrestsSort] = useState("newest");
  const [notifArrestsFilterHasReady, setNotifArrestsFilterHasReady] = useState("all");
  const [sendingNotif, setSendingNotif] = useState(false);
  const [notifConfirm, setNotifConfirm] = useState(null); // { type, item, readyHeadline, message }

  // Debounced search handler
  const handleSearchInputChange = (value) => {
    setSearchInput(value);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setSearchQuery(value);
    }, 500);
  };

  const [totalProfiles, setTotalProfiles] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const PAGE_LIMIT = 50;

  const fetchProfiles = useCallback(async (page = 1, append = false) => {
    try {
      if (!append) setLoading(true);
      const params = new URLSearchParams();
      if (searchQuery && searchField !== "incident") {
        params.append("search", searchQuery);
        if (searchField !== "all") {
          params.append("search_field", searchField);
        }
      }
      // Board view shares filter state with Profiles tab but does its own
      // client-side filtering (needed for the map view). Skip server-side
      // filters when Board is active so the local filtering has the full
      // profile set to work with.
      const isBoard = activeTab === "board";
      if (!isBoard && filterStatus !== "all") params.append("status", filterStatus);
      if (!isBoard && filterIncidentType !== "all") params.append("incident_type", filterIncidentType);
      if (!isBoard && filterDateFrom) params.append("date_from", filterDateFrom);
      if (!isBoard && filterDateTo) params.append("date_to", filterDateTo);
      if (!isBoard && filterHasArrest === "yes") params.append("has_arrest", "true");
      if (!isBoard && filterHasReady === "yes") params.append("has_ready", "true");
      params.append("page", page.toString());
      params.append("limit", PAGE_LIMIT.toString());

      const response = await axios.get(`${API}/perpetrator-profiles?${params}`, { withCredentials: true });
      const result = response.data;
      // Support both new paginated format {profiles, total} and legacy array format
      let data = Array.isArray(result) ? result : (result.profiles || []);
      const total = Array.isArray(result) ? data.length : (result.total || 0);

      if (!isBoard && filterHasArrest === "no") data = data.filter(p => !p.arrest_count || p.arrest_count === 0);
      if (!isBoard && filterHasReady === "no") data = data.filter(p => !p.ready_for_arrest_count || p.ready_for_arrest_count === 0);
      if (!isBoard && filterHasLinked === "yes") data = data.filter(p => p.linked_incident_count && p.linked_incident_count > 0);
      if (!isBoard && filterHasLinked === "no") data = data.filter(p => !p.linked_incident_count || p.linked_incident_count === 0);
      if (!isBoard && filterArrestVia === "yes") data = data.filter(p => p.arrest_via_count && p.arrest_via_count > 0);
      if (!isBoard && filterArrestVia === "no") data = data.filter(p => !p.arrest_via_count || p.arrest_via_count === 0);
      if (!isBoard && searchQuery && searchField === "incident") {
        data = data.filter(p => p.linked_incidents?.some(inc => inc.toLowerCase().includes(searchQuery.toLowerCase())));
      }

      if (append) {
        setProfiles(prev => [...prev, ...data]);
      } else {
        setProfiles(data);
      }
      setTotalProfiles(total);
      setCurrentPage(page);
      setHasMore(page * PAGE_LIMIT < total);
      initialLoadDone.current = true;
    } catch (error) {
      toast.error("Failed to load profiles");
    } finally {
      setLoading(false);
    }
  }, [searchQuery, searchField, filterStatus, filterIncidentType, filterDateFrom, filterDateTo, filterHasArrest, filterHasReady, filterHasLinked, filterArrestVia, activeTab]);

  // Sort profiles
  const sortedProfiles = useMemo(() => {
    const sorted = [...profiles];
    switch (profileSort) {
      case "newest":
        return sorted.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
      case "oldest":
        return sorted.sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
      case "name_az":
        return sorted.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      case "name_za":
        return sorted.sort((a, b) => (b.name || '').localeCompare(a.name || ''));
      case "most_linked":
        return sorted.sort((a, b) => (b.linked_incident_count || 0) - (a.linked_incident_count || 0));
      case "most_ready":
        return sorted.sort((a, b) => (b.ready_for_arrest_count || 0) - (a.ready_for_arrest_count || 0));
      case "most_arrests":
        return sorted.sort((a, b) => (b.arrest_count || 0) - (a.arrest_count || 0));
      case "recent_activity":
        return sorted.sort((a, b) => new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0));
      default:
        return sorted;
    }
  }, [profiles, profileSort]);

  const fetchIncidentTypes = async () => {
    try {
      const response = await axios.get(`${API}/incident-types`, { withCredentials: true });
      setIncidentTypes(response.data || []);
    } catch (error) {}
  };

  const fetchActivityLogs = async () => {
    try {
      const response = await axios.get(`${API}/perpetrator-profiles/activity/all?limit=500`, { withCredentials: true });
      setActivityLogs(response.data || []);
    } catch (error) {}
  };

  const fetchMapData = async () => {
    setMapLoading(true);
    try {
      const response = await axios.get(`${API}/perpetrator-profiles/board/map-data`, { withCredentials: true });
      setMapLocations(response.data?.locations || []);
    } catch (error) {
      console.error("Failed to load map data:", error);
    } finally {
      setMapLoading(false);
    }
  };

  const fetchProfileDetails = async (profileId) => {
    try {
      const response = await axios.get(`${API}/perpetrator-profiles/${profileId}`, { withCredentials: true });
      setSelectedProfile(response.data);
      setDetailTab("linked");
    } catch (error) {
      toast.error("Failed to load profile details");
    }
  };

  // Fetch profiles when filters change
  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  // Auto-select profile from URL — supports both the path param
  // (`/perpetrators/:profileId`, used when arriving from incident detail)
  // and the legacy `?profile=` query param. Fetches the detail directly
  // rather than waiting for the profile to appear in the filtered list,
  // so the target opens regardless of the current Profiles-tab status
  // filter (e.g. default "Ready for Arrest").
  useEffect(() => {
    const targetId = pathProfileId || searchParams.get("profile");
    if (!targetId) return;
    if (selectedProfile?.profile_id === targetId) return;
    fetchProfileDetails(targetId);
    if (!pathProfileId && searchParams.get("profile")) {
      setSearchParams({}, { replace: true });
    }
  }, [pathProfileId, searchParams, selectedProfile, setSearchParams]);

  // Handle mobile back button for profile detail view
  const prepareNavigate = useBackButton(selectedProfile !== null, () => setSelectedProfile(null));

  const handleIncidentClick = (incidentId, e) => {
    if (e) e.stopPropagation();
    if (!hasIncidentsAccess) return;
    setPendingIncidentNav(incidentId);
  };

  const confirmIncidentNav = () => {
    if (pendingIncidentNav) {
      prepareNavigate();
      navigate(`/incidents/${pendingIncidentNav}`);
      setPendingIncidentNav(null);
    }
  };

  // Fetch incident types and activity logs only on mount
  useEffect(() => {
    fetchIncidentTypes();
    fetchActivityLogs();
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  const handleCreateProfile = async () => {
    try {
      const response = await axios.post(`${API}/perpetrator-profiles`, newProfile, { withCredentials: true });
      setProfiles([response.data, ...profiles]);
      setShowNewDialog(false);
      setNewProfile({ name: "", mugshot_url: "", known_address: "", affiliates: "", notes: "" });
      toast.success("Profile created");
      fetchActivityLogs();
    } catch (error) { toast.error("Failed to create profile"); }
  };

  const handleUpdateProfile = async () => {
    try {
      const response = await axios.put(`${API}/perpetrator-profiles/${editProfile.profile_id}`, {
        name: editProfile.name, mugshot_url: editProfile.mugshot_url, known_address: editProfile.known_address,
        affiliates: editProfile.affiliates, notes: editProfile.notes, status: editProfile.status, dob: editProfile.dob
      }, { withCredentials: true });
      setProfiles(profiles.map(p => p.profile_id === editProfile.profile_id ? response.data : p));
      setSelectedProfile(response.data);
      setShowEditDialog(false);
      toast.success("Profile updated");
      fetchActivityLogs();
    } catch (error) { toast.error("Failed to update profile"); }
  };

  const handleMugshotUpload = async (e, isEdit = false) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("show_in_media_library", "true");
      const response = await axios.post(`${API}/upload`, formData, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      const url = response.data.url;
      if (isEdit) setEditProfile({ ...editProfile, mugshot_url: url });
      else setNewProfile({ ...newProfile, mugshot_url: url });
      toast.success("Photo uploaded");
    } catch (error) { toast.error("Failed to upload photo"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const handleIdLicenseUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedProfile) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("profile_id", selectedProfile.profile_id);
      formData.append("media_type", "id_license");
      const response = await axios.post(`${API}/upload`, formData, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      const updated = await axios.put(`${API}/perpetrator-profiles/${selectedProfile.profile_id}`, { id_license_url: response.data.url }, { withCredentials: true });
      setSelectedProfile(updated.data);
      setProfiles(profiles.map(p => p.profile_id === updated.data.profile_id ? updated.data : p));
      toast.success("ID/License uploaded");
    } catch (error) { toast.error("Failed to upload ID/License"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  // Handle mugshot change for selected profile
  const handleMugshotChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedProfile) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("profile_id", selectedProfile.profile_id);
      formData.append("media_type", "mugshot");
      const response = await axios.post(`${API}/upload`, formData, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });
      const updated = await axios.put(`${API}/perpetrator-profiles/${selectedProfile.profile_id}`, { mugshot_url: response.data.url }, { withCredentials: true });
      setSelectedProfile(updated.data);
      setProfiles(profiles.map(p => p.profile_id === updated.data.profile_id ? updated.data : p));
      toast.success("Mugshot updated");
    } catch (error) { toast.error("Failed to update mugshot"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  // Delete mugshot
  const handleDeleteMugshot = async () => {
    if (!selectedProfile) return;
    try {
      const updated = await axios.put(`${API}/perpetrator-profiles/${selectedProfile.profile_id}`, { mugshot_url: "" }, { withCredentials: true });
      setSelectedProfile(updated.data);
      setProfiles(profiles.map(p => p.profile_id === updated.data.profile_id ? updated.data : p));
      setShowDeleteMugshotDialog(false);
      toast.success("Mugshot deleted");
    } catch (error) { toast.error("Failed to delete mugshot"); }
  };

  // Delete ID/License
  const handleDeleteIdLicense = async () => {
    if (!selectedProfile) return;
    try {
      const updated = await axios.put(`${API}/perpetrator-profiles/${selectedProfile.profile_id}`, { id_license_url: "" }, { withCredentials: true });
      setSelectedProfile(updated.data);
      setProfiles(profiles.map(p => p.profile_id === updated.data.profile_id ? updated.data : p));
      setShowDeleteIdLicenseDialog(false);
      toast.success("ID/License deleted");
    } catch (error) { toast.error("Failed to delete ID/License"); }
  };

  // Helper function to capture video frame as thumbnail
  const captureVideoThumbnail = (file) => {
    return new Promise((resolve) => {
      const video = document.createElement('video');
      video.preload = 'auto';
      video.muted = true;
      video.playsInline = true;
      video.crossOrigin = 'anonymous';
      let hasResolved = false;
      const cleanup = () => { if (!hasResolved) { hasResolved = true; URL.revokeObjectURL(video.src); } };
      const timeout = setTimeout(() => { cleanup(); resolve(null); }, 10000);
      video.onloadedmetadata = () => { video.currentTime = Math.min(1, video.duration * 0.1); };
      video.onseeked = () => {
        clearTimeout(timeout);
        try {
          const canvas = document.createElement('canvas');
          canvas.width = video.videoWidth || 640;
          canvas.height = video.videoHeight || 360;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          canvas.toBlob((blob) => { cleanup(); resolve(blob); }, 'image/jpeg', 0.85);
        } catch (err) { cleanup(); resolve(null); }
      };
      video.onerror = () => { clearTimeout(timeout); cleanup(); resolve(null); };
      video.src = URL.createObjectURL(file);
      video.load();
    });
  };

  const handleMediaUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedProfile) return;
    const isVideoFile = file.type.startsWith('video/');
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("profile_id", selectedProfile.profile_id);
      formData.append("media_type", "profile_media");
      const response = await axios.post(`${API}/upload`, formData, { withCredentials: true, headers: { "Content-Type": "multipart/form-data" } });

      // If it's a video, capture and upload thumbnail
      if (isVideoFile && response.data.file_id) {
        try {
          const thumbnailBlob = await captureVideoThumbnail(file);
          if (thumbnailBlob) {
            const thumbFormData = new FormData();
            thumbFormData.append('file', thumbnailBlob, 'thumbnail.jpg');
            await axios.post(`${API}/upload/${response.data.file_id}/thumbnail`, thumbFormData, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
          }
        } catch (thumbError) { console.error("Thumbnail upload failed:", thumbError); }
      }

      fetchProfileMedia(selectedProfile.profile_id);
      toast.success("Media uploaded");
    } catch (error) { toast.error("Failed to upload media"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const fetchProfileMedia = async (profileId) => {
    try {
      const response = await axios.get(`${API}/uploads/profile/${profileId}`, { withCredentials: true });
      setProfileMedia(response.data);
    } catch (error) { setProfileMedia([]); }
  };

  const handleDeleteMedia = async (fileId) => {
    if (!selectedProfile || !window.confirm("Delete this media?")) return;
    try {
      await axios.delete(`${API}/uploads/profile/${selectedProfile.profile_id}/${fileId}`, { withCredentials: true });
      setProfileMedia(profileMedia.filter(m => m.file_id !== fileId));
      toast.success("Media deleted");
    } catch (error) { toast.error("Failed to delete media"); }
  };

  const handleExportProfiles = () => {
    const headers = ["Profile ID", "Name", "Status", "Known Address", "Affiliates", "Linked", "Ready", "Arrests", "Created"];
    const rows = profiles.map(p => [p.profile_id, p.name, p.status || "none", p.known_address || "", p.affiliates || "", p.linked_incident_count || 0, p.ready_for_arrest_count || 0, p.arrest_count || 0, p.created_at || ""]);
    const csv = [headers.join(","), ...rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `profiles_${new Date().toISOString().split('T')[0]}.csv`; a.click();
    toast.success(`Exported ${profiles.length} profiles (filtered)`);
  };

  const handleExportActivity = () => {
    let filtered = activityLogs.filter(l => {
      if (activityDateFrom && l.timestamp < activityDateFrom) return false;
      if (activityDateTo && l.timestamp > activityDateTo + "T23:59:59") return false;
      return true;
    });
    const headers = ["Timestamp", "Profile", "Action", "Details", "User"];
    const rows = filtered.map(l => [l.timestamp, l.profile_name, l.action, l.details || "", l.user_name]);
    const csv = [headers.join(","), ...rows.map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(","))].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `activity_${new Date().toISOString().split('T')[0]}.csv`; a.click();
    toast.success(`Exported ${filtered.length} activity logs`);
  };

  const clearFilters = () => {
    setSearchInput(""); setSearchQuery(""); setSearchField("all"); setFilterStatus("all"); setFilterHasArrest("all");
    setFilterHasReady("all"); setFilterHasLinked("all"); setFilterArrestVia("all"); setFilterIncidentType("all"); setIncidentTypeSearch(""); setFilterDateFrom(""); setFilterDateTo("");
  };

  const getStatusBadge = (status) => {
    const opt = STATUS_OPTIONS.find(o => o.value === status) || STATUS_OPTIONS[0];
    return <Badge className={`${opt.color} text-white text-xs`}>{opt.label}</Badge>;
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "";
  const formatShortDate = (d) => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "";

  // Render color-coded offense badges for a profile
  const renderOffenseBadges = (profile, size = "normal") => {
    const types = profile.incident_types || [];
    if (types.length === 0) return <span className="text-slate-500">—</span>;
    const arrestTypes = new Set(profile.arrest_incident_types || []);
    const readyTypes = new Set(profile.ready_incident_types || []);
    const cls = size === "small" ? "text-[8px] px-1 py-0" : size === "compact" ? "text-[10px] px-1.5 py-0.5" : "text-[10px] px-1.5 py-0.5";
    return (
      <div className="flex flex-wrap gap-1">
        {types.map(t => {
          const color = arrestTypes.has(t) ? "bg-emerald-600 text-white" : readyTypes.has(t) ? "bg-amber-600 text-white" : "dark:bg-blue-600/40 bg-blue-100 dark:text-blue-300 text-blue-700";
          return <span key={t} className={`inline-block rounded font-semibold uppercase ${cls} ${color}`}>{t}</span>;
        })}
      </div>
    );
  };

  const getAllLinkedIncidents = () => {
    if (!selectedProfile) return [];
    // Build lookup maps for ready and arrest records by incident_id
    const readyMap = {};
    (selectedProfile.ready_for_arrest || []).forEach(rfa => {
      if (rfa.incident_id) readyMap[rfa.incident_id] = rfa;
    });
    const arrestMap = {};
    (selectedProfile.arrests || []).forEach(arr => {
      if (arr.incident_id) arrestMap[arr.incident_id] = arr;
    });
    // Also build a map from ready record id -> arrest that linked to it
    const readyIdToArrest = {};
    (selectedProfile.arrests || []).forEach(arr => {
      const linkedIds = arr.linked_ready_for_arrest_ids || (arr.linked_ready_for_arrest_id ? [arr.linked_ready_for_arrest_id] : []);
      linkedIds.forEach(rfaId => { readyIdToArrest[rfaId] = arr; });
    });

    const incidents = [], seenIds = new Set();
    (selectedProfile.linked_incidents_details || []).forEach(inc => {
      if (!seenIds.has(inc.incident_id)) {
        seenIds.add(inc.incident_id);
        const rfa = readyMap[inc.incident_id];
        const arr = arrestMap[inc.incident_id];
        // Check if this incident's ready record was resolved by an arrest from a different incident
        const linkedArrest = rfa ? (readyIdToArrest[rfa.id] || readyIdToArrest[rfa.incident_id]) : null;
        incidents.push({ ...inc, source: "linked", hasReady: !!rfa, hasArrest: !!arr, arrestDate: arr?.arrest_date, linkedArrestIncidentId: (linkedArrest && linkedArrest.incident_id !== inc.incident_id) ? linkedArrest.incident_id : null, linkedArrestHeadline: linkedArrest?.headline });
      }
    });
    (selectedProfile.ready_for_arrest || []).forEach(rfa => {
      if (rfa.incident_id && !seenIds.has(rfa.incident_id)) {
        seenIds.add(rfa.incident_id);
        const arr = arrestMap[rfa.incident_id];
        const linkedArrest = readyIdToArrest[rfa.id] || readyIdToArrest[rfa.incident_id];
        incidents.push({ incident_id: rfa.incident_id, headline: rfa.headline, incident_type: rfa.incident_type, secondary_incident_type: rfa.secondary_incident_type, address: rfa.address, date_time: rfa.date_time, source: "ready", hasReady: true, hasArrest: !!arr, arrestDate: arr?.arrest_date, linkedArrestIncidentId: (linkedArrest && linkedArrest.incident_id !== rfa.incident_id) ? linkedArrest.incident_id : null, linkedArrestHeadline: linkedArrest?.headline });
      }
    });
    (selectedProfile.arrests || []).forEach(arr => {
      if (arr.incident_id && !seenIds.has(arr.incident_id)) {
        seenIds.add(arr.incident_id);
        incidents.push({ incident_id: arr.incident_id, headline: arr.headline, incident_type: arr.incident_type, secondary_incident_type: arr.secondary_incident_type, address: arr.address, date_time: arr.date_time, source: "arrest", hasReady: false, hasArrest: true, arrestDate: arr.arrest_date });
      }
    });
    return incidents.sort((a, b) => new Date(b.date_time || 0) - new Date(a.date_time || 0));
  };

  // Build the plain-text Quick Export summary for the selected incidents.
  // For each, fetches the full incident (callbacks/victims/witnesses) and
  // joins everything into a paste-friendly block.
  const buildQuickExportText = async (selectedIds) => {
    if (!selectedProfile) return "";
    const ids = Array.from(selectedIds || []);
    if (ids.length === 0) return "";

    // Look up arrest + ready records on the profile by incident_id so we can
    // determine status and pull arrest_linked headline/date for "Arrest Via".
    const arrestSet = new Set((selectedProfile.arrests || []).map(a => a.incident_id));
    const readyMap = new Map();
    (selectedProfile.ready_for_arrest || []).forEach(r => {
      if (r?.incident_id) readyMap.set(r.incident_id, r);
    });

    const formatDateTime = (iso) => {
      if (!iso) return "";
      try {
        return new Date(iso).toLocaleString("en-US", {
          timeZone: "America/New_York",
          month: "2-digit", day: "2-digit", year: "numeric",
          hour: "numeric", minute: "2-digit", hour12: true,
        });
      } catch { return iso; }
    };
    const looksLikePhone = (s) => !!s && /\d{7,}/.test(String(s).replace(/\D/g, ""));
    const formatPhone = (raw) => {
      const digits = String(raw || "").replace(/\D/g, "");
      const d = digits.length === 11 && digits.startsWith("1") ? digits.slice(1) : digits;
      if (d.length === 10) return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
      return raw;
    };

    // Fetch each incident in parallel.
    const fetched = await Promise.allSettled(
      ids.map(id => axios.get(`${API}/incidents/${id}`, { withCredentials: true }))
    );

    const blocks = [];
    fetched.forEach((res, i) => {
      const incidentId = ids[i];
      if (res.status !== "fulfilled" || !res.value?.data) {
        blocks.push(`${incidentId} — (failed to load)`);
        return;
      }
      const inc = res.value.data;
      const lines = [];

      // Header: incident_id — Type / Secondary (headline intentionally
      // omitted per dispatch request).
      const typeLabel = [inc.incident_type, inc.secondary_incident_type || inc.secondary_call_type]
        .filter(Boolean).join(" / ") || "Incident";
      lines.push(`${incidentId} — ${typeLabel}`);

      // Status (resolved against the profile's records).
      const rfa = readyMap.get(incidentId);
      const hasArrest = arrestSet.has(incidentId);
      const arrestVia = rfa && rfa.arrest_linked && rfa.arrest_linked.incident_id ? rfa.arrest_linked : null;
      let status;
      if (hasArrest) status = "Arrested";
      else if (rfa && arrestVia) status = "Ready for Arrest (Arrest Via)";
      else if (rfa) status = "Ready for Arrest";
      else status = "Linked";
      lines.push(`Status: ${status}`);

      if (inc.address) {
        lines.push(`Location: ${inc.address}`);
      }
      const when = formatDateTime(inc.date_time || inc.created_at);
      if (when) lines.push(`Date/Time: ${when}`);

      if (arrestVia) {
        const aviaWhen = formatDateTime(arrestVia.arrest_date || arrestVia.date_time);
        const aviaHead = arrestVia.headline || arrestVia.incident_id;
        lines.push(`Arrest Via: ${aviaHead}${aviaWhen ? ` · ${aviaWhen}` : ""}`);
      }

      // Callbacks — primary (legacy field) + callback_numbers, deduped.
      const callbackLines = [];
      const seenCb = new Set();
      const pushCb = (number, type) => {
        const key = String(number || "").replace(/\D/g, "");
        if (!key || seenCb.has(key)) return;
        seenCb.add(key);
        let labelBits = [];
        if (type === "cv") labelBits.push("CV");
        else if (type === "3rd_party" || type === "third_party") labelBits.push("3rd Party");
        callbackLines.push(`  • ${formatPhone(number)}${labelBits.length ? ` (${labelBits.join(", ")})` : ""}`);
      };
      if (inc.callback_phone) pushCb(inc.callback_phone, inc.cv_or_third_party);
      (inc.callback_numbers || []).forEach(cb => pushCb(cb.number, cb.caller_type || cb.type));
      if (callbackLines.length) {
        lines.push("");
        lines.push("Callbacks:");
        lines.push(...callbackLines);
      }

      // Helper: append unique phone numbers from a list of candidate
      // fields. Victim/witness records often have the same phone stored
      // in both phone1 and the legacy "contact" field (normalizer
      // populates both on save), so dedupe on digits-only.
      const collectUniquePhones = (...candidates) => {
        const out = [];
        const seen = new Set();
        candidates.flat().forEach(raw => {
          if (!looksLikePhone(raw)) return;
          const key = String(raw).replace(/\D/g, "");
          if (!key || seen.has(key)) return;
          seen.add(key);
          out.push(`  • ${formatPhone(raw)}`);
        });
        return out;
      };

      // Victim phone numbers
      const victimCandidates = [];
      if (inc.primary_victim_contact) victimCandidates.push(inc.primary_victim_contact);
      (inc.victims || []).forEach(v => {
        victimCandidates.push(v.phone1, v.phone2, v.contact, v.contact_info);
      });
      const victimLines = collectUniquePhones(victimCandidates);
      if (victimLines.length) {
        lines.push("");
        lines.push("Victims:");
        lines.push(...victimLines);
      }

      // Witness phone numbers
      const witnessCandidates = [];
      if (inc.primary_witness_contact) witnessCandidates.push(inc.primary_witness_contact);
      (inc.witnesses || []).forEach(w => {
        witnessCandidates.push(w.phone1, w.contact_info, w.contact);
      });
      const witnessLines = collectUniquePhones(witnessCandidates);
      if (witnessLines.length) {
        lines.push("");
        lines.push("Witnesses:");
        lines.push(...witnessLines);
      }

      blocks.push(lines.join("\n"));
    });

    const profileHeader = `Profile: ${selectedProfile.name || selectedProfile.profile_id}`;
    return [profileHeader, "", blocks.join("\n\n---\n\n")].join("\n");
  };

  const handleQuickExportCopy = async () => {
    if (!hasIncidentsAccess) return;
    if (quickExportSelectedIds.size === 0) {
      toast.error("Select at least one incident");
      return;
    }
    setQuickExportBusy(true);
    try {
      const text = await buildQuickExportText(quickExportSelectedIds);
      setQuickExportPreview(text);
      let copied = false;
      try {
        if (navigator.clipboard && window.isSecureContext) {
          await navigator.clipboard.writeText(text);
          copied = true;
        }
      } catch {}
      if (!copied) {
        try {
          const ta = document.createElement("textarea");
          ta.value = text;
          ta.style.position = "fixed";
          ta.style.opacity = "0";
          document.body.appendChild(ta);
          ta.select();
          document.execCommand("copy");
          document.body.removeChild(ta);
          copied = true;
        } catch {}
      }
      if (copied) {
        toast.success(`Copied ${quickExportSelectedIds.size} incident${quickExportSelectedIds.size === 1 ? "" : "s"} to clipboard`);
      } else {
        toast.error("Couldn't copy automatically — preview is below for manual copy");
      }
    } catch (err) {
      console.error("Quick Export failed:", err);
      toast.error("Failed to build export");
    } finally {
      setQuickExportBusy(false);
    }
  };

  const prepareNotification = (type, item, readyHeadline = null) => {
    if (!selectedProfile) return;
    const profileName = selectedProfile.name;
    const headline = item.headline || item.incident_type || item.incident_id;
    let message = "";
    if (type === "linked") {
      message = `"${profileName}" LINKED to "${headline}"`;
    } else if (type === "ready") {
      message = `"${profileName}" READY FOR ARREST in regards to "${headline}". If seen, advise central.`;
    } else if (type === "arrest") {
      message = readyHeadline
        ? `"${profileName}" ARRESTED in connection with "${readyHeadline}"`
        : `"${profileName}" ARRESTED in connection with "${headline}"`;
    }
    setNotifConfirm({ type, item, readyHeadline, message });
  };

  const confirmSendNotification = async () => {
    if (!notifConfirm || !selectedProfile) return;
    const { type, item, readyHeadline, message } = notifConfirm;
    setSendingNotif(true);
    try {
      await axios.post(`${API}/perpetrator-profiles/send-notification`, {
        profile_id: selectedProfile.profile_id,
        profile_name: selectedProfile.name,
        notification_type: type,
        incident_id: item.incident_id,
        incident_headline: item.headline || item.incident_type || item.incident_id,
        ready_incident_headline: readyHeadline,
        custom_message: message
      }, { withCredentials: true });
      toast.success("Notification sent!");
      setNotifConfirm(null);
      setShowNotifDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send notification");
    } finally {
      setSendingNotif(false);
    }
  };

  const hasActiveFilters = (filterStatus !== "all" && filterStatus !== "ready_for_arrest") || filterIncidentType !== "all" || filterDateFrom || filterDateTo || searchQuery || filterHasArrest !== "all" || filterHasReady !== "all" || filterHasLinked !== "all" || filterArrestVia !== "all";

  // Removed full-page blocking spinner — loading indicator is now inline in the list

  return (
    <div className="h-[calc(100dvh-8rem)] overflow-hidden flex flex-col">
      {/* Header */}
      <div className="flex-shrink-0 flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <UserCircle className="w-6 h-6 text-purple-500" />
          <h1 className="text-xl sm:text-2xl font-bold uppercase tracking-tight dark:text-white text-slate-900">Profiles</h1>
          <Badge variant="outline">{totalProfiles > 0 ? `${profiles.length} / ${totalProfiles}` : `${profiles.length}`} Profiles</Badge>
          {!isAdmin && <Badge variant="secondary">Read Only</Badge>}
        </div>
        <div className="flex items-center gap-2">
          {isAdmin && <Button variant="outline" size="sm" onClick={handleExportProfiles}><Download className="w-4 h-4 mr-1" />Export ({profiles.length})</Button>}
          {isAdmin && <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
            <DialogTrigger asChild><Button className="bg-purple-600 hover:bg-purple-700 text-white"><Plus className="w-4 h-4 mr-1" />Add Profile</Button></DialogTrigger>
            <DialogContent className="dark:bg-slate-900 bg-white dark:border-slate-800 border-slate-200">
              <DialogHeader><DialogTitle className="uppercase tracking-wider dark:text-white text-slate-900">Add New Profile</DialogTitle></DialogHeader>
              <div className="space-y-4 mt-4">
                <div><Label className="dark:text-slate-300 text-slate-700">Name</Label><Input value={newProfile.name} onChange={(e) => setNewProfile({ ...newProfile, name: e.target.value })} className="dark:bg-slate-800 bg-slate-100 dark:border-slate-700 border-slate-300" /></div>
                <div><Label className="dark:text-slate-300 text-slate-700">Mugshot</Label><div className="flex gap-2 items-center">{newProfile.mugshot_url && <img src={`${BACKEND_URL}${newProfile.mugshot_url}`} alt="" className="w-12 h-12 object-cover rounded" />}<label className="flex-1"><input type="file" accept="image/*" onChange={(e) => handleMugshotUpload(e, false)} className="hidden" /><Button variant="outline" className="w-full" disabled={uploading} asChild><span><Upload className="w-4 h-4 mr-1" />{uploading ? "..." : "Upload"}</span></Button></label></div></div>
                <div><Label className="dark:text-slate-300 text-slate-700">Known Address</Label><Input value={newProfile.known_address} onChange={(e) => setNewProfile({ ...newProfile, known_address: e.target.value })} className="dark:bg-slate-800 bg-slate-100 dark:border-slate-700 border-slate-300" /></div>
                <div><Label className="dark:text-slate-300 text-slate-700">Affiliates</Label><Input value={newProfile.affiliates} onChange={(e) => setNewProfile({ ...newProfile, affiliates: e.target.value })} className="dark:bg-slate-800 bg-slate-100 dark:border-slate-700 border-slate-300" /></div>
                <div><Label className="dark:text-slate-300 text-slate-700">Notes</Label><Textarea value={newProfile.notes} onChange={(e) => setNewProfile({ ...newProfile, notes: e.target.value })} className="dark:bg-slate-800 bg-slate-100 dark:border-slate-700 border-slate-300" rows={2} /></div>
                <Button onClick={handleCreateProfile} className="w-full bg-purple-600 hover:bg-purple-700 text-white">Create Profile</Button>
              </div>
            </DialogContent>
          </Dialog>}
        </div>
      </div>

      {/* Main Tabs - Activity tab only for admins */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
        <TabsList className="dark:bg-slate-900 bg-slate-100 w-fit mb-4">
          <TabsTrigger value="profiles"><Users className="w-4 h-4 mr-1" />Profiles</TabsTrigger>
          <TabsTrigger value="board"><AlertTriangle className="w-4 h-4 mr-1" />Board</TabsTrigger>
          {isAdmin && <TabsTrigger value="activity"><Activity className="w-4 h-4 mr-1" />Activity</TabsTrigger>}
        </TabsList>

        <TabsContent value="profiles" className="flex-1 overflow-hidden m-0">
          <div className="flex flex-col lg:flex-row h-full gap-4">
            {/* Left Panel */}
            <div className={`w-full lg:w-1/3 dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-lg flex flex-col overflow-hidden h-[calc(100vh-180px)] lg:h-full ${selectedProfile ? 'hidden lg:flex' : ''}`}>
              <div className="p-3 border-b dark:border-slate-800 border-slate-200 space-y-2">
                <div className="flex gap-2">
                  <Select value={searchField} onValueChange={setSearchField}>
                    <SelectTrigger className="w-28 dark:bg-slate-800 bg-slate-100 dark:border-slate-700 border-slate-300"><SelectValue /></SelectTrigger>
                    <SelectContent>{SEARCH_FIELDS.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
                  </Select>
                  <div className="relative flex-1"><Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" /><Input value={searchInput} onChange={(e) => handleSearchInputChange(e.target.value)} placeholder="Search..." className="pl-9 dark:bg-slate-800 bg-slate-100 dark:border-slate-700 border-slate-300" /></div>
                </div>
                <div className="flex gap-2">
                  <Select value={filterStatus} onValueChange={setFilterStatus}>
                    <SelectTrigger className={`flex-1 ${filterStatus !== "all" ? "dark:bg-amber-900/30 bg-amber-100 dark:border-amber-600 border-amber-400 dark:text-amber-300 text-amber-700" : "dark:bg-slate-800 bg-slate-100 dark:border-slate-700 border-slate-300"}`}><SelectValue placeholder="Status" /></SelectTrigger>
                    <SelectContent><SelectItem value="all">All Statuses</SelectItem>{STATUS_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                  </Select>
                  <Select value={profileSort} onValueChange={setProfileSort}>
                    <SelectTrigger className="w-28 dark:bg-slate-800 bg-slate-100 dark:border-slate-700 border-slate-300"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="newest">Newest</SelectItem>
                      <SelectItem value="oldest">Oldest</SelectItem>
                      <SelectItem value="name_az">Name A-Z</SelectItem>
                      <SelectItem value="name_za">Name Z-A</SelectItem>
                      <SelectItem value="most_linked">Most Linked</SelectItem>
                      <SelectItem value="most_ready">Most Ready</SelectItem>
                      <SelectItem value="most_arrests">Most Arrests</SelectItem>
                      <SelectItem value="recent_activity">Recent Activity</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button variant="outline" size="icon" onClick={() => setShowFilters(!showFilters)} className={showFilters ? "dark:bg-slate-700 bg-slate-200" : ""}>
                    <Filter className="w-4 h-4" />{hasActiveFilters && <span className="absolute -top-1 -right-1 w-2 h-2 bg-purple-500 rounded-full" />}
                  </Button>
                </div>
                {showFilters && (
                  <div className="space-y-2 pt-2 border-t dark:border-slate-700 border-slate-200">
                    <div className="grid grid-cols-3 gap-2">
                      <Select value={filterHasLinked} onValueChange={setFilterHasLinked}>
                        <SelectTrigger className="text-xs h-8 dark:bg-slate-800 bg-slate-100"><SelectValue placeholder="Linked" /></SelectTrigger>
                        <SelectContent><SelectItem value="all">Any Linked</SelectItem><SelectItem value="yes">Has Linked</SelectItem><SelectItem value="no">No Linked</SelectItem></SelectContent>
                      </Select>
                      <Select value={filterHasReady} onValueChange={setFilterHasReady}>
                        <SelectTrigger className="text-xs h-8 dark:bg-slate-800 bg-slate-100"><SelectValue placeholder="Ready" /></SelectTrigger>
                        <SelectContent><SelectItem value="all">Any Ready</SelectItem><SelectItem value="yes">Has Ready</SelectItem><SelectItem value="no">No Ready</SelectItem></SelectContent>
                      </Select>
                      <Select value={filterHasArrest} onValueChange={setFilterHasArrest}>
                        <SelectTrigger className="text-xs h-8 dark:bg-slate-800 bg-slate-100"><SelectValue placeholder="Arrests" /></SelectTrigger>
                        <SelectContent><SelectItem value="all">Any Arrests</SelectItem><SelectItem value="yes">Has Arrest</SelectItem><SelectItem value="no">No Arrest</SelectItem></SelectContent>
                      </Select>
                      <Select value={filterArrestVia} onValueChange={setFilterArrestVia}>
                        <SelectTrigger className="text-xs h-8 dark:bg-slate-800 bg-slate-100"><SelectValue placeholder="Arrest Via" /></SelectTrigger>
                        <SelectContent><SelectItem value="all">Any Arrest Via</SelectItem><SelectItem value="yes">Has Arrest Via</SelectItem><SelectItem value="no">No Arrest Via</SelectItem></SelectContent>
                      </Select>
                    </div>
                    <div className="flex gap-2">
                      <Input
                        placeholder="Search call type..."
                        value={incidentTypeSearch}
                        onChange={(e) => setIncidentTypeSearch(e.target.value)}
                        className="text-xs h-8 flex-1 dark:bg-slate-800 bg-slate-100"
                      />
                      <Select value={filterIncidentType} onValueChange={(v) => { setFilterIncidentType(v); setIncidentTypeSearch(""); }}>
                        <SelectTrigger className="text-xs h-8 w-32 dark:bg-slate-800 bg-slate-100"><SelectValue placeholder="Call Type" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">All Types</SelectItem>
                          {incidentTypes
                            .filter(t => !incidentTypeSearch || t.name.toLowerCase().includes(incidentTypeSearch.toLowerCase()))
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map(t => <SelectItem key={t.name} value={t.name}>{t.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Input type="date" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)} className="text-xs h-8 dark:bg-slate-800 bg-slate-100" placeholder="From" />
                      <Input type="date" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)} className="text-xs h-8 dark:bg-slate-800 bg-slate-100" placeholder="To" />
                    </div>
                    <Button variant="ghost" size="sm" onClick={clearFilters} className="w-full h-7 text-xs"><X className="w-3 h-3 mr-1" />Clear Filters</Button>
                  </div>
                )}
              </div>
              <ScrollArea className="flex-1 p-2 overflow-y-auto">
                {loading && profiles.length === 0 ? (
                  <div className="space-y-2 p-2">
                    {[...Array(8)].map((_, i) => (
                      <div key={i} className="p-3 rounded-lg flex items-center gap-3 animate-pulse">
                        <div className="w-10 h-10 dark:bg-slate-700 bg-slate-200 rounded" />
                        <div className="flex-1 space-y-2">
                          <div className="h-4 dark:bg-slate-700 bg-slate-200 rounded w-2/3" />
                          <div className="h-3 dark:bg-slate-700 bg-slate-200 rounded w-1/3" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <>
                    {sortedProfiles.map((p) => (
                      <div key={p.profile_id} onClick={() => fetchProfileDetails(p.profile_id)} className={`p-3 rounded-lg mb-1 cursor-pointer flex items-center gap-3 ${selectedProfile?.profile_id === p.profile_id ? "dark:bg-purple-900/30 bg-purple-100 border-2 border-purple-500" : "dark:hover:bg-slate-800 hover:bg-slate-100 border border-transparent"}`}>
                        {p.mugshot_url ? <img src={getThumbUrl(p.mugshot_url, 80)} alt="" className="w-10 h-10 object-cover rounded" loading="lazy" onError={(e) => e.target.style.display='none'} /> : <div className="w-10 h-10 dark:bg-slate-700 bg-slate-200 rounded flex items-center justify-center"><UserCircle className="w-5 h-5 text-slate-400" /></div>}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate dark:text-white text-slate-900">{p.name}</p>
                          <div className="flex items-center gap-1 mt-0.5 flex-wrap">{getStatusBadge(p.status)}{p.linked_incident_count > 0 && <Badge variant="outline" className="text-xs"><LinkIcon className="w-2.5 h-2.5 mr-0.5" />{p.linked_incident_count}</Badge>}{p.ready_for_arrest_count > 0 && <Badge variant="outline" className="text-xs text-amber-600 border-amber-500">{p.ready_for_arrest_count}</Badge>}{p.arrest_count > 0 && <Badge variant="outline" className="text-xs text-emerald-600 border-emerald-500">{p.arrest_count}</Badge>}</div>
                        </div>
                      </div>
                    ))}
                    {hasMore && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="w-full mt-2 text-xs text-slate-400"
                        onClick={() => fetchProfiles(currentPage + 1, true)}
                        disabled={loading}
                      >
                        {loading ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-purple-500 mr-2" /> : null}
                        Load More ({totalProfiles - profiles.length} remaining)
                      </Button>
                    )}
                    {profiles.length === 0 && !loading && <p className="text-center text-slate-500 py-8">No profiles found</p>}
                    <div className="h-20" />
                  </>
                )}
              </ScrollArea>
            </div>

            {/* Right Panel */}
            <div className={`w-full lg:w-2/3 dark:bg-slate-900 bg-white border dark:border-slate-800 border-slate-200 rounded-lg h-full overflow-y-auto ${selectedProfile ? 'block' : 'hidden lg:block'}`}>
              {selectedProfile ? (
                <div className="p-4 pb-24 sm:pb-4 flex flex-col">
                  <Button variant="ghost" size="sm" className="lg:hidden mb-3 w-fit" onClick={() => setSelectedProfile(null)}><X className="w-4 h-4 mr-1" />Back</Button>
                  {/* Header */}
                  <div className="mb-4 pb-4 border-b dark:border-slate-800 border-slate-200">
                    {/* Photos Row */}
                    <div className="flex items-start justify-between gap-3 mb-3">
                      <div className="flex gap-3">
                        {/* Mugshot */}
                        <div className="flex flex-col items-center gap-1">
                          {isAdmin && <input type="file" ref={mugshotInputRef} className="hidden" accept="image/*" onChange={handleMugshotChange} />}
                          {selectedProfile.mugshot_url ? (
                            <div
                              className="relative group cursor-pointer overflow-hidden rounded-lg"
                              onClick={() => { setLightboxImage(getThumbUrl(selectedProfile.mugshot_url, 1600)); setLightboxMediaType('image'); setLightboxHideLogo(false); }}
                            >
                              <img src={getThumbUrl(selectedProfile.mugshot_url, 400)} alt="" className="w-20 h-20 sm:w-24 sm:h-24 object-cover" draggable="false" />
                              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                <span className="text-white text-xs">Click to enlarge</span>
                              </div>
                            </div>
                          ) : isAdmin ? (
                            <div className="w-20 h-20 sm:w-24 sm:h-24 dark:bg-slate-800 bg-slate-200 rounded-lg flex items-center justify-center cursor-pointer hover:bg-slate-300 dark:hover:bg-slate-700 border-2 border-dashed border-slate-400" onClick={() => mugshotInputRef.current?.click()}>
                              <Upload className="w-8 h-8 sm:w-10 sm:h-10 text-slate-400" />
                            </div>
                          ) : (
                            <div className="w-20 h-20 sm:w-24 sm:h-24 dark:bg-slate-800 bg-slate-200 rounded-lg flex items-center justify-center"><UserCircle className="w-8 h-8 sm:w-10 sm:h-10 text-slate-400" /></div>
                          )}
                          <span className="text-[10px] sm:text-xs text-slate-500">Mugshot</span>
                          {isAdmin && selectedProfile.mugshot_url && (
                            <div className="flex gap-1">
                              <button onClick={(e) => { e.stopPropagation(); mugshotInputRef.current?.click(); }} className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700" title="Change"><RefreshCw className="w-3 h-3 text-slate-500" /></button>
                              <button onClick={(e) => { e.stopPropagation(); setShowDeleteMugshotDialog(true); }} className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30" title="Delete"><Trash2 className="w-3 h-3 text-red-500" /></button>
                            </div>
                          )}
                        </div>
                        {/* ID/License */}
                        <div className="flex flex-col items-center gap-1">
                          {isAdmin && <input type="file" ref={idLicenseInputRef} className="hidden" accept="image/*" onChange={handleIdLicenseUpload} />}
                          {selectedProfile.id_license_url ? (
                            <div
                              className="relative group cursor-pointer overflow-hidden rounded-lg"
                              onClick={() => { setLightboxImage(getThumbUrl(selectedProfile.id_license_url, 1600)); setLightboxMediaType('image'); setLightboxHideLogo(true); }}
                            >
                              <img src={getThumbUrl(selectedProfile.id_license_url, 200)} alt="ID" className="w-20 h-20 sm:w-24 sm:h-24 object-cover" draggable="false" />
                              <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                                <span className="text-white text-xs">Click to enlarge</span>
                              </div>
                            </div>
                          ) : isAdmin ? (
                            <div className="w-20 h-20 sm:w-24 sm:h-24 dark:bg-slate-800 bg-slate-200 rounded-lg flex items-center justify-center cursor-pointer hover:bg-slate-300 dark:hover:bg-slate-700 border-2 border-dashed border-slate-400" onClick={() => idLicenseInputRef.current?.click()}>
                              <Upload className="w-8 h-8 sm:w-10 sm:h-10 text-slate-400" />
                            </div>
                          ) : (
                            <div className="w-20 h-20 sm:w-24 sm:h-24 dark:bg-slate-800 bg-slate-200 rounded-lg flex items-center justify-center text-slate-500 text-xs">No ID</div>
                          )}
                          <span className="text-[10px] sm:text-xs text-slate-500">ID/License</span>
                          {isAdmin && selectedProfile.id_license_url && (
                            <div className="flex gap-1">
                              <button onClick={(e) => { e.stopPropagation(); idLicenseInputRef.current?.click(); }} className="p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-700" title="Change"><RefreshCw className="w-3 h-3 text-slate-500" /></button>
                              <button onClick={(e) => { e.stopPropagation(); setShowDeleteIdLicenseDialog(true); }} className="p-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30" title="Delete"><Trash2 className="w-3 h-3 text-red-500" /></button>
                            </div>
                          )}
                        </div>
                      </div>
                      {/* Edit (top) + Quick Export (below) — stacked so the
                          two profile-level actions sit one above the other. */}
                      <div className="flex flex-col items-end gap-1.5 flex-shrink-0">
                        <Button variant="outline" size="sm" onClick={() => { setEditProfile({ ...selectedProfile }); setShowEditDialog(true); }}><Edit className="w-4 h-4 mr-1" />Edit</Button>
                        {hasIncidentsAccess && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setQuickExportSelectedIds(new Set());
                              setQuickExportPreview("");
                              setShowQuickExport(true);
                            }}
                            title="Copy a text summary of selected incidents (callbacks, victims, witnesses)"
                          >
                            <Download className="w-4 h-4 mr-1" />Quick Export
                          </Button>
                        )}
                      </div>
                    </div>
                    {/* Name Row */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h2 className="text-xl sm:text-2xl font-bold dark:text-white text-slate-900">{selectedProfile.name}</h2>
                          {isAdmin && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 w-7 p-0 text-purple-400 hover:text-purple-300 hover:bg-purple-900/20 flex-shrink-0"
                              onClick={() => setShowNotifDialog(true)}
                              title="Send Notification"
                            >
                              <Bell className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          {getStatusBadge(selectedProfile.status)}
                        </div>
                        <p className="text-[10px] sm:text-xs text-slate-500 font-mono mt-1">{selectedProfile.profile_id}</p>
                        {selectedProfile.dob && <p className="text-xs text-slate-400 mt-1">DOB: {formatDob(selectedProfile.dob)}</p>}
                      </div>
                    </div>
                  </div>
                  {/* Stats */}
                  <div className="grid grid-cols-3 gap-3 mb-4">
                    <div className="dark:bg-slate-800 bg-slate-100 p-3 rounded-lg text-center"><p className="text-2xl font-bold dark:text-white text-slate-900">{getAllLinkedIncidents().length}</p><p className="text-xs text-slate-500 uppercase">Linked</p></div>
                    <div className="dark:bg-amber-900/30 bg-amber-50 p-3 rounded-lg text-center border dark:border-amber-700/30 border-amber-200"><p className="text-2xl font-bold text-amber-600">{(selectedProfile.ready_for_arrest || []).length}</p><p className="text-xs text-amber-600 uppercase">Ready</p></div>
                    <div className="dark:bg-emerald-900/30 bg-emerald-50 p-3 rounded-lg text-center border dark:border-emerald-700/30 border-emerald-200"><p className="text-2xl font-bold text-emerald-600">{(selectedProfile.arrests || []).length}</p><p className="text-xs text-emerald-600 uppercase">Arrests</p></div>
                  </div>
                  {/* Info */}
                  {(selectedProfile.dob || selectedProfile.known_address || selectedProfile.affiliates || selectedProfile.notes) && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                      {selectedProfile.dob && <div className="p-3 dark:bg-slate-800/50 bg-slate-50 rounded-lg"><p className="text-xs text-slate-500 uppercase mb-1">DOB</p><p className="text-sm dark:text-white text-slate-900">{formatDob(selectedProfile.dob)}</p></div>}
                      {selectedProfile.known_address && <div className="p-3 dark:bg-slate-800/50 bg-slate-50 rounded-lg"><p className="text-xs text-slate-500 uppercase mb-1"><MapPin className="w-3 h-3 inline mr-1" />Address</p><p className="text-sm dark:text-white text-slate-900">{selectedProfile.known_address}</p></div>}
                      {selectedProfile.affiliates && <div className="p-3 dark:bg-slate-800/50 bg-slate-50 rounded-lg"><p className="text-xs text-slate-500 uppercase mb-1"><Users className="w-3 h-3 inline mr-1" />Affiliates</p><p className="text-sm dark:text-white text-slate-900">{selectedProfile.affiliates}</p></div>}
                      {selectedProfile.notes && <div className="p-3 dark:bg-slate-800/50 bg-slate-50 rounded-lg sm:col-span-2"><p className="text-xs text-slate-500 uppercase mb-1"><FileText className="w-3 h-3 inline mr-1" />Notes</p><p className="text-sm dark:text-white text-slate-900 whitespace-pre-wrap">{selectedProfile.notes}</p></div>}
                    </div>
                  )}
                  {/* Detail Tabs */}
                  <Tabs value={detailTab} onValueChange={(v) => { setDetailTab(v); if (v === "media") fetchProfileMedia(selectedProfile.profile_id); }} className="flex-1 flex flex-col overflow-hidden">
                    <div className="overflow-x-auto shrink-0">
                      <TabsList className="dark:bg-slate-800 bg-slate-100 w-max">
                        <TabsTrigger value="linked" className="text-xs">Linked ({getAllLinkedIncidents().length})</TabsTrigger>
                        <TabsTrigger value="ready" className="text-xs">Ready ({(selectedProfile.ready_for_arrest || []).length})</TabsTrigger>
                        <TabsTrigger value="arrests" className="text-xs">Arrests ({(selectedProfile.arrests || []).length})</TabsTrigger>
                        <TabsTrigger value="media" className="text-xs">Media ({profileMedia.length})</TabsTrigger>
                      </TabsList>
                    </div>
                    <TabsContent value="linked" className="flex-1 overflow-hidden m-0 mt-2">
                      {getAllLinkedIncidents().length > 0 && (
                        <div className="flex gap-2 mb-2">
                          <Input placeholder="Search..." value={linkedSearch} onChange={(e) => setLinkedSearch(e.target.value)} className="h-8 text-xs flex-1 dark:bg-slate-800 bg-slate-100" />
                          <Select value={linkedSort} onValueChange={setLinkedSort}>
                            <SelectTrigger className="w-24 h-8 text-xs dark:bg-slate-800 bg-slate-100"><SelectValue /></SelectTrigger>
                            <SelectContent><SelectItem value="newest">Newest</SelectItem><SelectItem value="oldest">Oldest</SelectItem><SelectItem value="type">Type</SelectItem></SelectContent>
                          </Select>
                        </div>
                      )}
                      <ScrollArea className="h-full">{getAllLinkedIncidents().length === 0 ? <p className="text-slate-500 text-center py-8">No linked incidents</p> : <div className="space-y-2 pr-2">{getAllLinkedIncidents()
                        .filter(inc => !linkedSearch || (inc.headline || '').toLowerCase().includes(linkedSearch.toLowerCase()) || (inc.incident_type || '').toLowerCase().includes(linkedSearch.toLowerCase()) || (inc.secondary_incident_type || '').toLowerCase().includes(linkedSearch.toLowerCase()) || (inc.address || '').toLowerCase().includes(linkedSearch.toLowerCase()) || (inc.incident_id || '').toLowerCase().includes(linkedSearch.toLowerCase()))
                        .sort((a, b) => linkedSort === "newest" ? new Date(b.date_time || 0) - new Date(a.date_time || 0) : linkedSort === "oldest" ? new Date(a.date_time || 0) - new Date(b.date_time || 0) : (a.incident_type || '').localeCompare(b.incident_type || ''))
                        .map((inc, i) => (
                        <div key={inc.incident_id || i} onClick={() => handleIncidentClick(inc.incident_id)} className={`p-3 rounded-lg border-l-[3px] overflow-hidden ${inc.hasArrest ? "border-l-emerald-500 dark:bg-emerald-900/10 bg-emerald-50/50" : inc.hasReady ? "border-l-amber-500 dark:bg-amber-900/10 bg-amber-50/50" : "border-l-blue-500 dark:bg-slate-800/50 bg-slate-50"} border dark:border-slate-700/50 border-slate-200 ${hasIncidentsAccess ? "cursor-pointer dark:hover:bg-slate-800 hover:bg-slate-100" : ""}`}>
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <Badge variant="outline" className="text-xs shrink-0">{inc.incident_type}</Badge>
                            {inc.secondary_incident_type && <Badge variant="outline" className="text-xs shrink-0 dark:border-slate-600 border-slate-400 text-slate-500">+ {inc.secondary_incident_type}</Badge>}
                            {inc.hasReady && <Badge className="bg-amber-500 text-white text-xs shrink-0">Ready</Badge>}
                            {inc.hasArrest && <Badge className="bg-emerald-500 text-white text-xs shrink-0">Arrested</Badge>}
                            {inc.linkedArrestIncidentId && <Badge className="bg-emerald-700 text-white text-xs shrink-0 cursor-pointer hover:bg-emerald-600" onClick={(e) => { e.stopPropagation(); handleIncidentClick(inc.linkedArrestIncidentId); }}>Arrest Via ↗</Badge>}
                            {!inc.hasReady && !inc.hasArrest && !inc.linkedArrestIncidentId && <Badge className="bg-blue-500 text-white text-xs shrink-0">Linked</Badge>}
                          </div>
                          <p className="font-medium dark:text-white text-slate-900 break-words">{inc.headline || inc.incident_id}</p>
                          <p className="text-xs text-slate-500 break-words">{inc.address} • {formatShortDate(inc.date_time)}</p>
                          {inc.hasArrest && inc.arrestDate && <p className="text-xs text-emerald-500 mt-1">Arrested: {formatShortDate(inc.arrestDate)}</p>}
                          {inc.linkedArrestIncidentId && (
                            <p className="text-xs text-emerald-400 mt-1 cursor-pointer hover:underline" onClick={(e) => { e.stopPropagation(); handleIncidentClick(inc.linkedArrestIncidentId); }}>
                              {inc.linkedArrestHeadline || inc.linkedArrestIncidentId} →
                            </p>
                          )}
                        </div>
                      ))}</div>}</ScrollArea>
                    </TabsContent>
                    <TabsContent value="ready" className="flex-1 overflow-hidden m-0 mt-2">
                      {(selectedProfile.ready_for_arrest || []).length > 0 && (
                        <div className="space-y-2 mb-2">
                          <div className="flex gap-2">
                            <Input placeholder="Search headline, address..." value={readySearch} onChange={(e) => setReadySearch(e.target.value)} className="h-8 text-xs flex-1 dark:bg-slate-800 bg-slate-100" />
                            <Select value={readySort} onValueChange={setReadySort}>
                              <SelectTrigger className="w-24 h-8 text-xs dark:bg-slate-800 bg-slate-100"><SelectValue /></SelectTrigger>
                              <SelectContent><SelectItem value="newest">Newest</SelectItem><SelectItem value="oldest">Oldest</SelectItem><SelectItem value="type">Type</SelectItem></SelectContent>
                            </Select>
                          </div>
                          <div className="flex gap-2 flex-wrap">
                            <Select value={readyFilterArrested} onValueChange={setReadyFilterArrested}>
                              <SelectTrigger className="w-28 h-7 text-xs dark:bg-slate-800 bg-slate-100"><SelectValue placeholder="Arrest" /></SelectTrigger>
                              <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="yes">Arrested</SelectItem><SelectItem value="no">Not Arrested</SelectItem></SelectContent>
                            </Select>
                            <Select value={readyFilterReport} onValueChange={setReadyFilterReport}>
                              <SelectTrigger className="w-28 h-7 text-xs dark:bg-slate-800 bg-slate-100"><SelectValue placeholder="Report" /></SelectTrigger>
                              <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="yes">Report Filed</SelectItem><SelectItem value="no">No Report</SelectItem></SelectContent>
                            </Select>
                            <Select value={readyFilterType} onValueChange={setReadyFilterType}>
                              <SelectTrigger className="w-28 h-7 text-xs dark:bg-slate-800 bg-slate-100"><SelectValue placeholder="Type" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="all">All Types</SelectItem>
                                {[...new Set((selectedProfile.ready_for_arrest || []).flatMap(r => [r.incident_type, r.secondary_incident_type]).filter(Boolean))].sort().map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                              </SelectContent>
                            </Select>
                            <Select value={readyFilterIdMethod} onValueChange={setReadyFilterIdMethod}>
                              <SelectTrigger className="w-28 h-7 text-xs dark:bg-slate-800 bg-slate-100"><SelectValue placeholder="ID Method" /></SelectTrigger>
                              <SelectContent>
                                <SelectItem value="all">All Methods</SelectItem>
                                {[...new Set((selectedProfile.ready_for_arrest || []).flatMap(r => r.identification_methods || []))].sort().map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      )}
                      <ScrollArea className="h-full">{(selectedProfile.ready_for_arrest || []).length === 0 ? <p className="text-slate-500 text-center py-8">No ready for arrest</p> : <div className="space-y-2 pr-2">{(selectedProfile.ready_for_arrest || [])
                        .filter(rfa => {
                          // Text search - headline, address only
                          if (readySearch) {
                            const search = readySearch.toLowerCase();
                            if (!(rfa.headline || '').toLowerCase().includes(search) && !(rfa.address || '').toLowerCase().includes(search)) return false;
                          }
                          // Arrest filter
                          if (readyFilterArrested === "yes" && !rfa.arrest_linked) return false;
                          if (readyFilterArrested === "no" && rfa.arrest_linked) return false;
                          // Report filter
                          if (readyFilterReport === "yes" && !rfa.report_filed) return false;
                          if (readyFilterReport === "no" && rfa.report_filed) return false;
                          // Type filter
                          if (readyFilterType !== "all" && rfa.incident_type !== readyFilterType && rfa.secondary_incident_type !== readyFilterType) return false;
                          // ID Method filter
                          if (readyFilterIdMethod !== "all" && !(rfa.identification_methods || []).includes(readyFilterIdMethod)) return false;
                          return true;
                        })
                        .sort((a, b) => readySort === "newest" ? new Date(b.date_time || b.created_at || 0) - new Date(a.date_time || a.created_at || 0) : readySort === "oldest" ? new Date(a.date_time || a.created_at || 0) - new Date(b.date_time || b.created_at || 0) : (a.incident_type || '').localeCompare(b.incident_type || ''))
                        .map((rfa, i) => {
                          // Find linked arrest if exists
                          const linkedArrest = rfa.arrest_linked ? (selectedProfile.arrests || []).find(arr =>
                            arr.linked_ready_for_arrest_id === rfa.id ||
                            arr.linked_ready_for_arrest_id === rfa.incident_id ||
                            (arr.linked_ready_for_arrest_ids || []).includes(rfa.id) ||
                            (arr.linked_ready_for_arrest_ids || []).includes(rfa.incident_id)
                          ) : null;
                          return (
                        <div key={rfa.id || i} className="p-3 rounded-lg border dark:bg-amber-900/10 bg-amber-50 dark:border-amber-700/30 border-amber-200">
                          <div className="flex items-center justify-between mb-1 flex-wrap gap-1">
                            <div className="flex items-center gap-1 flex-wrap">
                              <Badge variant="outline" className="text-xs">{rfa.incident_type}</Badge>
                              {rfa.secondary_incident_type && <Badge variant="outline" className="text-xs dark:border-slate-600 border-slate-400 text-slate-500">+ {rfa.secondary_incident_type}</Badge>}
                              {rfa.report_filed && <Badge className="bg-blue-500 text-white text-xs">REPORT FILED</Badge>}
                            </div>
                            {rfa.arrest_linked && <Badge className="bg-emerald-500 text-white text-xs">ARRESTED</Badge>}
                          </div>
                          <p className="font-medium dark:text-white text-slate-900">{rfa.headline}</p>
                          <p className="text-xs text-slate-500"><MapPin className="w-3 h-3 inline mr-1" />{rfa.address} • {formatShortDate(rfa.date_time)}</p>
                          {rfa.identification_methods?.length > 0 && (
                            <div className="mt-2">
                              <p className="text-xs text-slate-500 mb-1">Identification:</p>
                              <div className="flex gap-1 flex-wrap">{rfa.identification_methods.map(m => <Badge key={m} variant="outline" className="text-xs">{m}</Badge>)}</div>
                            </div>
                          )}
                          {hasIncidentsAccess && <Button variant="link" size="sm" className="p-0 h-auto text-xs mt-2 text-blue-500" onClick={(e) => handleIncidentClick(rfa.incident_id, e)}>View Ready Incident <ExternalLink className="w-3 h-3 ml-1" /></Button>}

                          {/* Embedded Arrest Info */}
                          {linkedArrest && (
                            <div className="mt-3 pt-3 border-t dark:border-emerald-700/50 border-emerald-300">
                              <div onClick={(e) => handleIncidentClick(linkedArrest.incident_id, e)} className={`p-2 rounded dark:bg-emerald-900/30 bg-emerald-100 border dark:border-emerald-700/50 border-emerald-300 ${hasIncidentsAccess ? "cursor-pointer dark:hover:bg-emerald-900/50 hover:bg-emerald-200" : ""}`}>
                                <div className="flex items-center gap-1 mb-1">
                                  <Badge className="bg-emerald-600 text-white text-xs">ARREST</Badge>
                                  <Badge variant="outline" className="text-xs">{linkedArrest.incident_type}</Badge>
                                  {linkedArrest.secondary_incident_type && <Badge variant="outline" className="text-xs dark:border-slate-600 border-slate-400 text-slate-500">+ {linkedArrest.secondary_incident_type}</Badge>}
                                </div>
                                <p className="font-medium text-sm dark:text-white text-slate-900">{linkedArrest.headline}</p>
                                <p className="text-xs text-slate-500"><MapPin className="w-3 h-3 inline mr-1" />{linkedArrest.address}</p>
                                <p className="text-xs text-emerald-600 mt-1"><Calendar className="w-3 h-3 inline mr-1" />Arrested: {formatShortDate(linkedArrest.arrest_date)}</p>
                              </div>
                            </div>
                          )}
                        </div>
                        );})}</div>}</ScrollArea>
                    </TabsContent>
                    <TabsContent value="arrests" className="flex-1 overflow-hidden m-0 mt-2">
                      {(selectedProfile.arrests || []).length > 0 && (
                        <div className="flex gap-2 mb-2">
                          <Input placeholder="Search..." value={arrestsSearch} onChange={(e) => setArrestsSearch(e.target.value)} className="h-8 text-xs flex-1 dark:bg-slate-800 bg-slate-100" />
                          <Select value={arrestsSort} onValueChange={setArrestsSort}>
                            <SelectTrigger className="w-24 h-8 text-xs dark:bg-slate-800 bg-slate-100"><SelectValue /></SelectTrigger>
                            <SelectContent><SelectItem value="newest">Newest</SelectItem><SelectItem value="oldest">Oldest</SelectItem><SelectItem value="type">Type</SelectItem></SelectContent>
                          </Select>
                        </div>
                      )}
                      <ScrollArea className="h-full">{(selectedProfile.arrests || []).length === 0 ? <p className="text-slate-500 text-center py-8">No arrests</p> : <div className="space-y-2 pr-2">{(selectedProfile.arrests || [])
                        .filter(arr => !arrestsSearch || (arr.headline || '').toLowerCase().includes(arrestsSearch.toLowerCase()) || (arr.incident_type || '').toLowerCase().includes(arrestsSearch.toLowerCase()) || (arr.secondary_incident_type || '').toLowerCase().includes(arrestsSearch.toLowerCase()) || (arr.address || '').toLowerCase().includes(arrestsSearch.toLowerCase()))
                        .sort((a, b) => arrestsSort === "newest" ? new Date(b.arrest_date || b.date_time || 0) - new Date(a.arrest_date || a.date_time || 0) : arrestsSort === "oldest" ? new Date(a.arrest_date || a.date_time || 0) - new Date(b.arrest_date || b.date_time || 0) : (a.incident_type || '').localeCompare(b.incident_type || ''))
                        .map((arr, i) => (
                        <div key={arr.arrest_id || i} onClick={() => handleIncidentClick(arr.incident_id)} className={`p-3 dark:bg-emerald-900/20 bg-emerald-50 border dark:border-emerald-700/30 border-emerald-200 rounded-lg ${hasIncidentsAccess ? "cursor-pointer dark:hover:bg-emerald-900/40 hover:bg-emerald-100" : ""}`}>
                          <div className="flex items-center gap-1 flex-wrap mb-1">
                            <Badge variant="outline" className="text-xs">{arr.incident_type}</Badge>
                            {arr.secondary_incident_type && <Badge variant="outline" className="text-xs dark:border-slate-600 border-slate-400 text-slate-500">+ {arr.secondary_incident_type}</Badge>}
                          </div>
                          <p className="font-medium dark:text-white text-slate-900">{arr.headline}</p>
                          <p className="text-xs text-slate-500"><MapPin className="w-3 h-3 inline mr-1" />{arr.address}</p>
                          <p className="text-xs text-emerald-600 mt-1"><Calendar className="w-3 h-3 inline mr-1" />Arrested: {formatShortDate(arr.arrest_date)}</p>
                        </div>
                      ))}</div>}</ScrollArea>
                    </TabsContent>
                    <TabsContent value="media" className="flex-1 overflow-hidden m-0 mt-2">
                      <div className="mb-3">
                        <input type="file" ref={mediaUploadRef} className="hidden" accept="image/*,video/*" onChange={handleMediaUpload} />
                        <Button variant="outline" size="sm" onClick={() => mediaUploadRef.current?.click()} disabled={uploading}><Upload className="w-4 h-4 mr-1" />{uploading ? "Uploading..." : "Upload Media"}</Button>
                      </div>
                      <ScrollArea className="h-full">
                        {profileMedia.length === 0 ? <p className="text-slate-500 text-center py-8">No media uploaded</p> : (
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 pr-2">
                            {profileMedia.map(media => {
                              const isVideo = media.content_type?.startsWith('video/');
                              return (
                                <div key={media.file_id} className="relative group">
                                  <div
                                    className="aspect-square dark:bg-slate-800 bg-slate-200 rounded-lg overflow-hidden cursor-pointer"
                                    onClick={() => { setLightboxImage(`${BACKEND_URL}/uploads/${media.file_id}`); setLightboxMediaType(isVideo ? 'video' : 'image'); setLightboxHideLogo(false); }}
                                  >
                                    {isVideo ? (
                                      <div className="w-full h-full relative bg-slate-900">
                                        <video
                                          src={`${BACKEND_URL}/uploads/${media.file_id}`}
                                          className="w-full h-full object-cover"
                                          muted
                                          playsInline
                                          preload="metadata"
                                          onLoadedMetadata={(e) => { e.target.currentTime = 1; }}
                                        />
                                        <div className="absolute inset-0 flex items-center justify-center pointer-events-none bg-black/20">
                                          <div className="bg-black/50 rounded-full p-1.5">
                                            <Play className="w-5 h-5 text-white" />
                                          </div>
                                        </div>
                                      </div>
                                    ) : (
                                      <img src={getFileThumbUrl(media.file_id, 200)} alt={media.filename} className="w-full h-full object-cover pointer-events-none" draggable="false" />
                                    )}
                                    {/* Server-side watermarking handles non-admin users */}
                                  </div>
                                  {isAdmin && <button onClick={() => handleDeleteMedia(media.file_id)} className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"><X className="w-3 h-3" /></button>}
                                  <p className="text-xs text-slate-500 truncate mt-1">{media.filename}</p>
                                  <p className="text-xs text-slate-400">{media.uploaded_by_name}</p>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </ScrollArea>
                    </TabsContent>
                  </Tabs>
                  <div className="mt-3 pt-2 border-t dark:border-slate-800 border-slate-200 text-xs text-slate-500">Created: {formatDate(selectedProfile.created_at)}{isAdmin && selectedProfile.created_by_name ? ` by ${selectedProfile.created_by_name}` : ''}</div>
                </div>
              ) : (
                <div className="h-full flex items-center justify-center"><div className="text-center"><UserCircle className="w-16 h-16 mx-auto mb-4 text-slate-400" /><p className="text-slate-500">Select a profile</p></div></div>
              )}
            </div>
          </div>
        </TabsContent>

        {/* Board Tab - Crime Stoppers Style */}
        <TabsContent value="board" className="flex-1 overflow-hidden m-0">
          <div className="h-full flex flex-col dark:bg-[#1a1a2e] bg-white rounded-lg border dark:border-slate-800 border-slate-200 overflow-hidden">
            {/* Board Header */}
            <div className="p-4 border-b dark:border-red-800/50 border-red-200 flex-shrink-0">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-2xl sm:text-3xl font-black dark:text-white text-slate-900 tracking-tight uppercase" style={{ fontFamily: 'Impact, Arial Black, sans-serif' }}>WANTED</h2>
                <div className="flex items-center gap-2">
                  {boardView === "card" && (
                    <div className="flex items-center gap-0.5 dark:bg-slate-800 bg-slate-100 rounded p-0.5">
                      {[1, 2, 3].map(n => (
                        <button
                          key={n}
                          onClick={() => setBoardGridCols(n)}
                          className={`px-1.5 py-1 rounded text-[10px] font-bold transition-colors ${boardGridCols === n ? "bg-red-700 text-white" : "dark:text-slate-400 text-slate-500 dark:hover:text-white hover:text-slate-900"}`}
                          title={`${n} column${n > 1 ? 's' : ''}`}
                        >
                          {n}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center gap-1 dark:bg-slate-800 bg-slate-100 rounded p-0.5">
                    <button onClick={() => { setBoardView("map"); if (mapLocations.length === 0) fetchMapData(); }} className={`p-1.5 rounded transition-colors ${boardView === "map" ? "bg-red-700 text-white" : "dark:text-slate-400 text-slate-500 dark:hover:text-white hover:text-slate-900"}`} title="Map View"><MapPinned className="w-5 h-5" /></button>
                    <button onClick={() => setBoardView("list")} className={`p-1.5 rounded transition-colors ${boardView === "list" ? "bg-red-700 text-white" : "dark:text-slate-400 text-slate-500 dark:hover:text-white hover:text-slate-900"}`} title="List View"><List className="w-5 h-5" /></button>
                    <button onClick={() => setBoardView("card")} className={`p-1.5 rounded transition-colors ${boardView === "card" ? "bg-red-700 text-white" : "dark:text-slate-400 text-slate-500 dark:hover:text-white hover:text-slate-900"}`} title="Card View"><LayoutGrid className="w-5 h-5" /></button>
                  </div>
                </div>
              </div>
              <div className="h-0.5 bg-gradient-to-r from-red-700 via-red-500 to-transparent mb-3" />
              <button
                onClick={() => setBoardFiltersOpen(!boardFiltersOpen)}
                className="flex items-center gap-2 text-sm dark:text-slate-400 text-slate-500 dark:hover:text-white hover:text-slate-900 transition-colors w-full"
              >
                <Filter className="w-3.5 h-3.5" />
                <span className="font-semibold">Filters</span>
                {(filterStatus !== "all" || filterIncidentType !== "all" || filterDateFrom || filterDateTo || boardProfileFilter.length > 0) && (
                  <span className="w-2 h-2 rounded-full bg-red-500" />
                )}
                <span className="ml-auto text-xs text-slate-500">{(() => {
                  const filtered = profiles.filter(p => {
                    if (filterStatus !== "all" && p.status !== filterStatus) return false;
                    if (filterIncidentType !== "all" && !(p.incident_types || []).includes(filterIncidentType)) return false;
                    if (filterDateFrom && (p.created_at || "") < filterDateFrom) return false;
                    if (filterDateTo && (p.created_at || "") > filterDateTo + "T23:59:59") return false;
                    return true;
                  });
                  return `${filtered.length} profile${filtered.length !== 1 ? "s" : ""}`;
                })()}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${boardFiltersOpen ? "rotate-180" : ""}`} />
              </button>
              {boardFiltersOpen && (
                <div className="flex flex-wrap items-center gap-3 mt-3">
                  <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="dark:bg-slate-800 bg-slate-100 border dark:border-slate-600 border-slate-300 dark:text-white text-slate-900 px-3 py-1.5 rounded text-sm">
                    <option value="all">Status: All</option>
                    {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                  <select value={filterIncidentType} onChange={(e) => setFilterIncidentType(e.target.value)} className="dark:bg-slate-800 bg-slate-100 border dark:border-slate-600 border-slate-300 dark:text-white text-slate-900 px-3 py-1.5 rounded text-sm">
                    <option value="all">Offense: All</option>
                    {incidentTypes.map(t => {
                      const name = typeof t === "string" ? t : t.name;
                      return <option key={name} value={name}>{name}</option>;
                    })}
                  </select>
                  <select value={filterArrestVia} onChange={(e) => setFilterArrestVia(e.target.value)} className="dark:bg-slate-800 bg-slate-100 border dark:border-slate-600 border-slate-300 dark:text-white text-slate-900 px-3 py-1.5 rounded text-sm">
                    <option value="all">Arrest Via: All</option>
                    <option value="yes">Has Arrest Via</option>
                    <option value="no">No Arrest Via</option>
                  </select>
                  {/* Profiles multi-select — narrows all board views (especially
                      the map) to one or more specific profiles. */}
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setBoardProfileFilterOpen(o => !o)}
                      className="dark:bg-slate-800 bg-slate-100 border dark:border-slate-600 border-slate-300 dark:text-white text-slate-900 px-3 py-1.5 rounded text-sm flex items-center gap-1.5"
                    >
                      Profiles: {boardProfileFilter.length === 0 ? "All" : `${boardProfileFilter.length} selected`}
                      <ChevronDown className={`w-3.5 h-3.5 transition-transform ${boardProfileFilterOpen ? "rotate-180" : ""}`} />
                    </button>
                    {boardProfileFilterOpen && (
                      <div className="absolute z-30 mt-1 w-72 dark:bg-slate-900 bg-white border dark:border-slate-700 border-slate-300 rounded shadow-lg max-h-80 flex flex-col">
                        <input
                          type="text"
                          placeholder="Search profiles..."
                          value={boardProfileFilterSearch}
                          onChange={(e) => setBoardProfileFilterSearch(e.target.value)}
                          className="px-2 py-1.5 text-sm border-b dark:border-slate-700 border-slate-300 dark:bg-slate-800 bg-slate-50 dark:text-white text-slate-900 outline-none"
                          autoFocus
                        />
                        <div className="overflow-y-auto flex-1">
                          {profiles
                            .filter(p => !boardProfileFilterSearch || (p.name || p.profile_id || "").toLowerCase().includes(boardProfileFilterSearch.toLowerCase()))
                            .slice(0, 200)
                            .map(p => {
                              const checked = boardProfileFilter.includes(p.profile_id);
                              return (
                                <label key={p.profile_id} className="flex items-center gap-2 px-2 py-1.5 text-sm cursor-pointer dark:hover:bg-slate-800 hover:bg-slate-100">
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={(e) => {
                                      setBoardProfileFilter(prev => e.target.checked
                                        ? [...prev, p.profile_id]
                                        : prev.filter(id => id !== p.profile_id));
                                    }}
                                    className="cursor-pointer"
                                  />
                                  <span className="truncate dark:text-white text-slate-900">{p.name || p.profile_id}</span>
                                </label>
                              );
                            })}
                          {profiles.length === 0 && (
                            <p className="text-xs text-slate-500 px-2 py-2">No profiles loaded.</p>
                          )}
                        </div>
                        <div className="border-t dark:border-slate-700 border-slate-300 flex items-center justify-between px-2 py-1.5">
                          <button
                            type="button"
                            onClick={() => setBoardProfileFilter([])}
                            className="text-xs dark:text-slate-400 text-slate-500 dark:hover:text-white hover:text-slate-900 underline disabled:opacity-40 disabled:no-underline"
                            disabled={boardProfileFilter.length === 0}
                          >
                            Clear
                          </button>
                          <button
                            type="button"
                            onClick={() => setBoardProfileFilterOpen(false)}
                            className="text-xs dark:text-slate-300 text-slate-700 dark:hover:text-white hover:text-slate-900"
                          >
                            Done
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  <span className="dark:text-slate-400 text-slate-500 text-sm font-semibold">From:</span>
                  <input type="date" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)} className="dark:bg-slate-800 bg-slate-100 border dark:border-slate-600 border-slate-300 dark:text-white text-slate-900 px-2 py-1 rounded text-sm" />
                  <span className="dark:text-slate-400 text-slate-500 text-sm font-semibold">To:</span>
                  <input type="date" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)} className="dark:bg-slate-800 bg-slate-100 border dark:border-slate-600 border-slate-300 dark:text-white text-slate-900 px-2 py-1 rounded text-sm" />
                  {(filterStatus !== "all" || filterIncidentType !== "all" || filterArrestVia !== "all" || filterDateFrom || filterDateTo || boardProfileFilter.length > 0) && (
                    <button onClick={() => { setFilterStatus("all"); setFilterIncidentType("all"); setFilterArrestVia("all"); setFilterDateFrom(""); setFilterDateTo(""); setBoardProfileFilter([]); setBoardProfileFilterSearch(""); }} className="text-xs dark:text-slate-400 text-slate-500 dark:hover:text-white hover:text-slate-900 underline">Clear</button>
                  )}
                </div>
              )}
            </div>

            {/* Board Content */}
            <ScrollArea className="flex-1">
              {(() => {
                const boardProfiles = profiles.filter(p => {
                  if (filterStatus !== "all" && p.status !== filterStatus) return false;
                  if (filterIncidentType !== "all" && !(p.incident_types || []).includes(filterIncidentType)) return false;
                  if (filterDateFrom && (p.created_at || "") < filterDateFrom) return false;
                  if (filterDateTo && (p.created_at || "") > filterDateTo + "T23:59:59") return false;
                  if (filterArrestVia === "yes" && (!p.arrest_via_count || p.arrest_via_count === 0)) return false;
                  if (filterArrestVia === "no" && p.arrest_via_count && p.arrest_via_count > 0) return false;
                  if (boardProfileFilter.length > 0 && !boardProfileFilter.includes(p.profile_id)) return false;
                  return true;
                });

                if (boardProfiles.length === 0) {
                  return <div className="flex items-center justify-center h-64 text-slate-500">No profiles match current filters</div>;
                }

                if (boardView === "card") {
                  return (
                    <div
                      className={`grid p-4 ${boardGridCols === 3 ? 'grid-cols-3 gap-2' : boardGridCols === 2 ? 'grid-cols-2 gap-3' : 'grid-cols-1 gap-4'}`}
                      style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 6rem)' }}
                    >
                      {boardProfiles.map(p => {
                        const statusLabel = STATUS_OPTIONS.find(s => s.value === p.status)?.label || "Unknown";
                        const statusBorder = p.status === "arrested" ? "border-emerald-500 dark:border-emerald-500" : p.status === "ready_for_arrest" ? "border-amber-500 dark:border-amber-500" : p.status === "warrant" ? "border-red-500 dark:border-red-500" : p.status === "linked" ? "border-blue-500 dark:border-blue-500" : "dark:border-slate-700 border-slate-200";
                        return (
                          <div key={p.profile_id} onClick={() => openBoardPreview(p.profile_id)} className={`dark:bg-[#16213e] bg-slate-50 border-2 ${statusBorder} rounded-lg overflow-hidden cursor-pointer hover:shadow-lg dark:hover:shadow-red-900/20 hover:shadow-red-200 transition-all group`}>
                            {boardGridCols === 1 && (
                              <div className="p-3 pb-1">
                                <p className="text-xs dark:text-slate-400 text-slate-500 uppercase tracking-widest font-semibold mb-1">{statusLabel === "No Label" ? "PERSON OF INTEREST" : statusLabel.toUpperCase()}</p>
                                {renderOffenseBadges(p)}
                              </div>
                            )}
                            <div className={boardGridCols === 1 ? "px-3 pb-2" : "p-1.5 pb-1"}>
                              {p.mugshot_url ? (
                                <div className="w-full bg-black rounded overflow-hidden" style={{ aspectRatio: boardGridCols >= 3 ? '3/4' : boardGridCols === 2 ? '3/4' : '4/5' }}>
                                  <img src={getThumbUrl(p.mugshot_url, boardGridCols >= 3 ? 200 : 400)} alt="" className="w-full h-full object-contain" loading="lazy" onError={(e) => { e.target.style.display='none'; const p = e.target.parentElement; if (!p.querySelector('.no-img-label')) { p.classList.add('flex','items-center','justify-center'); const s = document.createElement('span'); s.className = 'text-slate-600 text-sm no-img-label'; s.textContent = 'No Image'; p.appendChild(s); } }} />
                                </div>
                              ) : (
                                <div className="w-full dark:bg-slate-800 bg-slate-200 rounded flex items-center justify-center" style={{ aspectRatio: boardGridCols >= 3 ? '3/4' : boardGridCols === 2 ? '3/4' : '4/5' }}>
                                  <UserCircle className={boardGridCols >= 3 ? "w-8 h-8 text-slate-400" : "w-16 h-16 text-slate-400"} />
                                </div>
                              )}
                            </div>
                            <div className={boardGridCols === 1 ? "px-3 pb-3" : "px-1.5 pb-1.5"}>
                              {p.name && <p className={`font-bold dark:text-white text-slate-900 truncate ${boardGridCols >= 3 ? 'text-[10px]' : boardGridCols === 2 ? 'text-xs' : 'text-sm mb-1'}`}>{p.name}</p>}
                              {boardGridCols === 1 && p.known_address && <p className="text-xs dark:text-slate-400 text-slate-500 truncate mb-1"><MapPin className="w-3 h-3 inline mr-1" />{p.known_address}</p>}
                              {boardGridCols >= 2 && (
                                <p className={`dark:text-slate-400 text-slate-500 uppercase truncate font-semibold ${boardGridCols >= 3 ? 'text-[8px]' : 'text-[10px]'}`}>{statusLabel === "No Label" ? "POI" : statusLabel.toUpperCase()}</p>
                              )}
                              {boardGridCols === 1 && (
                                <div className="flex items-center justify-between mt-2">
                                  <div className="flex items-center gap-1">
                                    {p.linked_incident_count > 0 && <Badge className="text-[10px] dark:bg-blue-600/30 bg-blue-100 dark:text-blue-300 text-blue-700 dark:border-blue-600 border-blue-300">{p.linked_incident_count} incident{p.linked_incident_count !== 1 ? "s" : ""}</Badge>}
                                    {p.arrest_count > 0 && <Badge className="text-[10px] dark:bg-emerald-600/30 bg-emerald-100 dark:text-emerald-300 text-emerald-700 dark:border-emerald-600 border-emerald-300">{p.arrest_count} arrest{p.arrest_count !== 1 ? "s" : ""}</Badge>}
                                  </div>
                                  <button className="px-2 py-1 bg-red-700 hover:bg-red-600 text-white text-[10px] font-bold rounded transition-colors uppercase tracking-wider" onClick={(e) => { e.stopPropagation(); openBoardPreview(p.profile_id); }}>
                                    View Details
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                }

                // Map view
                if (boardView === "map") {
                  const filteredMapLocs = mapLocations.filter(loc => {
                    if (!loc.lat || !loc.lng) return false;
                    if (filterStatus !== "all" && loc.status !== filterStatus) return false;
                    if (filterIncidentType !== "all" && !(loc.incident_types || []).includes(filterIncidentType)) return false;
                    if (boardProfileFilter.length > 0 && !boardProfileFilter.includes(loc.profile_id)) return false;
                    return true;
                  });

                  if (mapLoading) {
                    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500" /></div>;
                  }

                  return (
                    <div className="h-[calc(100vh-320px)] min-h-[400px] relative z-0">
                      <MapContainer center={[40.6501, -73.9496]} zoom={13} style={{ height: "100%", width: "100%", background: isDark !== false ? "#1a1a2e" : "#f0f0f0" }} className="rounded-b-lg">
                        <TileLayer
                          attribution='&copy; <a href="https://carto.com/">CARTO</a>'
                          url={isDark !== false
                            ? "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                            : "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                          }
                        />
                        <FitBoundsToMarkers locations={filteredMapLocs} />
                        {filteredMapLocs.map((loc, idx) => (
                          <Marker key={`${loc.profile_id}-${idx}`} position={[loc.lat, loc.lng]} icon={defaultMarkerIcon}>
                            <Popup>
                              <div className="text-center min-w-[150px]">
                                {loc.mugshot_url && (
                                  <img src={getThumbUrl(loc.mugshot_url, 400)} alt="" className="w-24 h-28 object-cover rounded mx-auto mb-2" />
                                )}
                                <p className="font-bold text-sm">{loc.name || "Unknown"}</p>
                                <p className="text-xs text-gray-600">{loc.address}</p>
                                {loc.incident_types?.length > 0 && <div className="mt-1">{renderOffenseBadges(loc, "small")}</div>}
                                <p className="text-xs text-gray-500">{loc.incident_count} incident{loc.incident_count !== 1 ? "s" : ""}</p>
                                <button className="mt-2 px-3 py-1 bg-red-600 text-white text-xs font-bold rounded" onClick={() => openBoardPreview(loc.profile_id)}>
                                  View Profile
                                </button>
                              </div>
                            </Popup>
                          </Marker>
                        ))}
                      </MapContainer>
                    </div>
                  );
                }

                // List view
                return (
                  <div
                    className="p-4"
                    style={{ paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 6rem)' }}
                  >
                    <table className="w-full">
                      <thead>
                        <tr className="border-b-2 dark:border-red-800/50 border-red-200">
                          <th className="text-left py-3 px-2 text-sm font-bold dark:text-slate-300 text-slate-600 uppercase tracking-wider w-24">Photo</th>
                          <th className="text-left py-3 px-2 text-sm font-bold dark:text-slate-300 text-slate-600 uppercase tracking-wider">Name / Offense</th>
                          <th className="text-left py-3 px-2 text-sm font-bold dark:text-slate-300 text-slate-600 uppercase tracking-wider hidden md:table-cell">Details</th>
                          <th className="text-left py-3 px-2 text-sm font-bold dark:text-slate-300 text-slate-600 uppercase tracking-wider">Status</th>
                          <th className="text-center py-3 px-2 text-sm font-bold dark:text-slate-300 text-slate-600 uppercase tracking-wider hidden sm:table-cell w-24"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {boardProfiles.map(p => {
                          const statusOpt = STATUS_OPTIONS.find(s => s.value === p.status);
                          return (
                            <tr key={p.profile_id} onClick={() => openBoardPreview(p.profile_id)} className="border-b dark:border-slate-800 border-slate-200 dark:hover:bg-slate-800/50 hover:bg-slate-50 cursor-pointer transition-colors">
                              <td className="py-3 px-2">
                                {p.mugshot_url ? (
                                  <img src={getThumbUrl(p.mugshot_url, 200)} alt="" className="w-16 h-20 object-cover rounded" loading="lazy" onError={(e) => e.target.style.display='none'} />
                                ) : (
                                  <div className="w-16 h-20 dark:bg-slate-800 bg-slate-200 rounded flex items-center justify-center"><UserCircle className="w-8 h-8 text-slate-400" /></div>
                                )}
                              </td>
                              <td className="py-3 px-2">
                                <p className="font-bold dark:text-white text-slate-900">{p.name || "Unknown"}</p>
                                <div className="mt-1">{renderOffenseBadges(p, "compact")}</div>
                              </td>
                              <td className="py-3 px-2 hidden md:table-cell">
                                <p className="text-sm dark:text-slate-400 text-slate-500 line-clamp-2">{p.notes || "—"}</p>
                                {p.linked_incident_count > 0 && <p className="text-xs text-blue-400 mt-1">{p.linked_incident_count} linked incident{p.linked_incident_count !== 1 ? "s" : ""}</p>}
                              </td>
                              <td className="py-3 px-2">
                                <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] sm:text-xs font-bold uppercase ${statusOpt?.color || "bg-slate-600"} text-white`}>{statusOpt?.label || p.status}</span>
                              </td>
                              <td className="py-3 px-2 text-center hidden sm:table-cell">
                                <button className="px-2 py-1 bg-red-700 hover:bg-red-600 text-white text-xs font-bold rounded transition-colors uppercase" onClick={(e) => { e.stopPropagation(); openBoardPreview(p.profile_id); }}>
                                  <Eye className="w-3 h-3 inline mr-1" />View
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
            </ScrollArea>
          </div>
        </TabsContent>

        <TabsContent value="activity" className="flex-1 overflow-hidden m-0">
          <Card className="dark:bg-slate-900 bg-white dark:border-slate-800 border-slate-200 h-full flex flex-col">
            <CardHeader className="pb-2 flex-shrink-0">
              <div className="space-y-2">
                <CardTitle className="text-sm flex items-center gap-2 dark:text-white text-slate-900"><Activity className="w-4 h-4" />Activity Log</CardTitle>
                <div className="flex flex-wrap items-center gap-2">
                  <Input type="date" value={activityDateFrom} onChange={(e) => setActivityDateFrom(e.target.value)} className="h-8 w-28 sm:w-32 text-xs dark:bg-slate-800 bg-slate-100" />
                  <Input type="date" value={activityDateTo} onChange={(e) => setActivityDateTo(e.target.value)} className="h-8 w-28 sm:w-32 text-xs dark:bg-slate-800 bg-slate-100" />
                  <Button variant="outline" size="sm" onClick={handleExportActivity} className="h-8"><Download className="w-3 h-3 mr-1" />Export</Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="flex-1 overflow-hidden"><ScrollArea className="h-full">{activityLogs.filter(l => { if (activityDateFrom && l.timestamp < activityDateFrom) return false; if (activityDateTo && l.timestamp > activityDateTo + "T23:59:59") return false; return true; }).length === 0 ? <p className="text-center py-8 text-slate-500">No activity</p> : <div className="space-y-2 pr-2">{activityLogs.filter(l => { if (activityDateFrom && l.timestamp < activityDateFrom) return false; if (activityDateTo && l.timestamp > activityDateTo + "T23:59:59") return false; return true; }).map(log => (<div key={log.log_id} className="p-3 dark:bg-slate-800 bg-slate-50 rounded-lg overflow-hidden"><div className="flex items-center justify-between mb-1 flex-wrap gap-1"><div className="flex items-center gap-2 min-w-0"><Badge variant="outline" className="text-xs shrink-0">{log.action}</Badge><span className="font-medium dark:text-white text-slate-900 truncate">{log.profile_name}</span></div><span className="text-xs text-slate-500 shrink-0">{formatDate(log.timestamp)}</span></div><p className="text-sm dark:text-slate-400 text-slate-600 break-words">{log.details}</p><p className="text-xs text-slate-500 mt-1">by {log.user_name}</p></div>))}</div>}</ScrollArea></CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Quick Export Dialog (incidents-access only) */}
      {hasIncidentsAccess && (
        <Dialog open={showQuickExport} onOpenChange={setShowQuickExport}>
          <DialogContent className="dark:bg-slate-900 bg-white dark:border-slate-800 border-slate-200 max-w-2xl">
            <DialogHeader>
              <DialogTitle className="uppercase tracking-wider dark:text-white text-slate-900 flex items-center gap-2">
                <Download className="w-5 h-5" />
                Quick Export — {selectedProfile?.name || selectedProfile?.profile_id}
              </DialogTitle>
            </DialogHeader>
            {(() => {
              const candidates = getAllLinkedIncidents();
              const allChecked = candidates.length > 0 && candidates.every(c => quickExportSelectedIds.has(c.incident_id));
              const toggleAll = () => {
                setQuickExportSelectedIds(new Set(allChecked ? [] : candidates.map(c => c.incident_id)));
              };
              const toggle = (id) => {
                setQuickExportSelectedIds(prev => {
                  const next = new Set(prev);
                  if (next.has(id)) next.delete(id); else next.add(id);
                  return next;
                });
              };
              return (
                <div className="space-y-3 mt-2">
                  <p className="text-xs text-slate-500">
                    Pick one or more incidents — the text summary covers every tab
                    (linked, ready, arrests). Click <em>Copy to Clipboard</em> to
                    paste into a report or message.
                  </p>
                  {candidates.length > 0 ? (
                    <>
                      <div className="flex items-center justify-between">
                        <button
                          type="button"
                          onClick={toggleAll}
                          className="text-xs text-blue-500 hover:underline"
                        >
                          {allChecked ? "Deselect all" : "Select all"} ({candidates.length})
                        </button>
                        <span className="text-xs text-slate-500">
                          {quickExportSelectedIds.size} selected
                        </span>
                      </div>
                      <div className="max-h-[40vh] overflow-y-auto border dark:border-slate-700 border-slate-300 rounded-md divide-y dark:divide-slate-800 divide-slate-200">
                        {candidates.map(inc => {
                          const checked = quickExportSelectedIds.has(inc.incident_id);
                          const tag = inc.hasArrest ? "Arrested" : inc.hasReady ? "Ready" : "Linked";
                          return (
                            <label
                              key={inc.incident_id}
                              className="flex items-start gap-3 p-2.5 cursor-pointer dark:hover:bg-slate-800/50 hover:bg-slate-50"
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => toggle(inc.incident_id)}
                                className="mt-0.5"
                              />
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <Badge variant="outline" className="text-[10px]">{inc.incident_type || "—"}</Badge>
                                  <Badge className={`text-[10px] ${
                                    tag === "Arrested" ? "bg-emerald-600" :
                                    tag === "Ready" ? "bg-amber-600" :
                                    "bg-blue-600"
                                  } text-white`}>{tag}</Badge>
                                  {inc.linkedArrestIncidentId && (
                                    <Badge className="text-[10px] bg-emerald-800 text-white">Arrest Via</Badge>
                                  )}
                                </div>
                                <p className="text-sm font-medium dark:text-white text-slate-900 mt-0.5 break-words">
                                  {inc.headline || inc.incident_id}
                                </p>
                                <p className="text-[11px] text-slate-500 break-words">
                                  {inc.address || "—"}{inc.date_time ? ` · ${formatShortDate(inc.date_time)}` : ""}
                                </p>
                                <p className="text-[10px] text-slate-500 font-mono">{inc.incident_id}</p>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    </>
                  ) : (
                    <p className="text-sm text-slate-500 text-center py-6">No incidents on this profile.</p>
                  )}

                  {quickExportPreview && (
                    <div>
                      <p className="text-xs text-slate-500 mb-1">Preview (already on your clipboard):</p>
                      <textarea
                        readOnly
                        value={quickExportPreview}
                        className="w-full h-40 text-xs font-mono dark:bg-slate-950 bg-slate-50 dark:text-slate-200 text-slate-800 border dark:border-slate-700 border-slate-300 rounded p-2"
                      />
                    </div>
                  )}

                  <div className="flex items-center justify-end gap-2 pt-2 border-t dark:border-slate-800 border-slate-200">
                    <Button variant="outline" size="sm" onClick={() => setShowQuickExport(false)}>Close</Button>
                    <Button
                      size="sm"
                      className="btn-primary"
                      onClick={handleQuickExportCopy}
                      disabled={quickExportBusy || quickExportSelectedIds.size === 0}
                    >
                      {quickExportBusy
                        ? <><RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" />Building…</>
                        : <><Copy className="w-3.5 h-3.5 mr-1" />Copy to Clipboard</>}
                    </Button>
                  </div>
                </div>
              );
            })()}
          </DialogContent>
        </Dialog>
      )}

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="dark:bg-slate-900 bg-white dark:border-slate-800 border-slate-200">
          <DialogHeader><DialogTitle className="uppercase tracking-wider dark:text-white text-slate-900">Edit Profile</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="dark:text-slate-300 text-slate-700">Name</Label><Input value={editProfile.name || ""} onChange={(e) => setEditProfile({ ...editProfile, name: e.target.value })} className="dark:bg-slate-800 bg-slate-100 dark:border-slate-700 border-slate-300" /></div>
              <div><Label className="dark:text-slate-300 text-slate-700">Date of Birth</Label><Input type="date" value={editProfile.dob || ""} onChange={(e) => setEditProfile({ ...editProfile, dob: e.target.value })} className="dark:bg-slate-800 bg-slate-100 dark:border-slate-700 border-slate-300" /></div>
            </div>
            <div><Label className="dark:text-slate-300 text-slate-700">Status</Label><Select value={editProfile.status || "none"} onValueChange={(v) => setEditProfile({ ...editProfile, status: v })}><SelectTrigger className="dark:bg-slate-800 bg-slate-100 dark:border-slate-700 border-slate-300"><SelectValue /></SelectTrigger><SelectContent>{STATUS_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="dark:text-slate-300 text-slate-700">Mugshot</Label><div className="flex gap-2 items-center">{editProfile.mugshot_url && <img src={`${BACKEND_URL}${editProfile.mugshot_url}`} alt="" className="w-12 h-12 object-cover rounded" />}<label className="flex-1"><input type="file" accept="image/*" onChange={(e) => handleMugshotUpload(e, true)} className="hidden" /><Button variant="outline" className="w-full" disabled={uploading} asChild><span><Upload className="w-4 h-4 mr-1" />{uploading ? "..." : "Upload"}</span></Button></label></div></div>
            <div><Label className="dark:text-slate-300 text-slate-700">Known Address</Label><Input value={editProfile.known_address || ""} onChange={(e) => setEditProfile({ ...editProfile, known_address: e.target.value })} className="dark:bg-slate-800 bg-slate-100 dark:border-slate-700 border-slate-300" /></div>
            <div><Label className="dark:text-slate-300 text-slate-700">Affiliates</Label><Input value={editProfile.affiliates || ""} onChange={(e) => setEditProfile({ ...editProfile, affiliates: e.target.value })} className="dark:bg-slate-800 bg-slate-100 dark:border-slate-700 border-slate-300" /></div>
            <div><Label className="dark:text-slate-300 text-slate-700">Notes</Label><Textarea value={editProfile.notes || ""} onChange={(e) => setEditProfile({ ...editProfile, notes: e.target.value })} className="dark:bg-slate-800 bg-slate-100 dark:border-slate-700 border-slate-300" rows={2} /></div>
            <Button onClick={handleUpdateProfile} className="w-full bg-purple-600 hover:bg-purple-700 text-white"><Save className="w-4 h-4 mr-1" />Save</Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Image Lightbox with Download */}
      {/* Notification Dialog */}
      <Dialog open={showNotifDialog} onOpenChange={setShowNotifDialog}>
        <DialogContent className="dark:bg-slate-900 bg-white dark:border-slate-800 border-slate-200 max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 dark:text-white text-slate-900">
              <Bell className="w-5 h-5 text-purple-500" />
              Send Notification - {selectedProfile?.name}
            </DialogTitle>
          </DialogHeader>
          <p className="text-xs text-slate-500">Select an incident to send a notification</p>

          <Tabs value={notifTab} onValueChange={setNotifTab}>
            <TabsList className="dark:bg-slate-800 bg-slate-100 w-fit">
              <TabsTrigger value="linked" className="text-xs">Linked ({getAllLinkedIncidents().length})</TabsTrigger>
              <TabsTrigger value="ready" className="text-xs">Ready ({(selectedProfile?.ready_for_arrest || []).length})</TabsTrigger>
              <TabsTrigger value="arrests" className="text-xs">Arrests ({(selectedProfile?.arrests || []).length})</TabsTrigger>
            </TabsList>

            {/* Linked Tab */}
            <TabsContent value="linked" className="m-0 mt-2">
              <div className="flex gap-2 mb-2">
                <Input placeholder="Search..." value={notifLinkedSearch} onChange={(e) => setNotifLinkedSearch(e.target.value)} className="h-8 text-xs flex-1 dark:bg-slate-800 bg-slate-100" />
                <Select value={notifLinkedSort} onValueChange={setNotifLinkedSort}>
                  <SelectTrigger className="w-24 h-8 text-xs dark:bg-slate-800 bg-slate-100"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="newest">Newest</SelectItem><SelectItem value="oldest">Oldest</SelectItem><SelectItem value="type">Type</SelectItem></SelectContent>
                </Select>
              </div>
              <ScrollArea className="h-[50vh]">
                {getAllLinkedIncidents().length === 0 ? <p className="text-slate-500 text-center py-8">No linked incidents</p> : (
                  <div className="space-y-2 pr-2">
                    {getAllLinkedIncidents()
                      .filter(inc => !notifLinkedSearch || (inc.headline || '').toLowerCase().includes(notifLinkedSearch.toLowerCase()) || (inc.incident_type || '').toLowerCase().includes(notifLinkedSearch.toLowerCase()) || (inc.address || '').toLowerCase().includes(notifLinkedSearch.toLowerCase()))
                      .sort((a, b) => notifLinkedSort === "newest" ? new Date(b.date_time || 0) - new Date(a.date_time || 0) : notifLinkedSort === "oldest" ? new Date(a.date_time || 0) - new Date(b.date_time || 0) : (a.incident_type || '').localeCompare(b.incident_type || ''))
                      .map((inc, i) => (
                        <div
                          key={inc.incident_id || i}
                          onClick={() => prepareNotification("linked", inc)}
                          className="p-3 dark:bg-slate-800 bg-slate-100 rounded-lg cursor-pointer hover:ring-2 hover:ring-purple-500 border dark:border-slate-700 border-slate-200"
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline" className="text-xs">{inc.incident_type}</Badge>
                          </div>
                          <p className="font-medium dark:text-white text-slate-900 truncate">{inc.headline || inc.incident_id}</p>
                          <p className="text-xs text-slate-500 truncate">{inc.address} • {formatShortDate(inc.date_time)}</p>
                          <p className="text-xs text-purple-400 mt-1 break-words">Click to send: "{selectedProfile?.name}" LINKED to "{inc.headline || inc.incident_type}"</p>
                        </div>
                      ))}
                  </div>
                )}
              </ScrollArea>
            </TabsContent>

            {/* Ready Tab */}
            <TabsContent value="ready" className="m-0 mt-2">
              <div className="space-y-2 mb-2">
                <div className="flex gap-2">
                  <Input placeholder="Search..." value={notifReadySearch} onChange={(e) => setNotifReadySearch(e.target.value)} className="h-8 text-xs flex-1 dark:bg-slate-800 bg-slate-100" />
                  <Select value={notifReadySort} onValueChange={setNotifReadySort}>
                    <SelectTrigger className="w-24 h-8 text-xs dark:bg-slate-800 bg-slate-100"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="newest">Newest</SelectItem><SelectItem value="oldest">Oldest</SelectItem><SelectItem value="type">Type</SelectItem></SelectContent>
                  </Select>
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Select value={notifReadyFilterArrested} onValueChange={setNotifReadyFilterArrested}>
                    <SelectTrigger className="w-28 h-7 text-xs dark:bg-slate-800 bg-slate-100"><SelectValue placeholder="Arrest" /></SelectTrigger>
                    <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="yes">Arrested</SelectItem><SelectItem value="no">Not Arrested</SelectItem></SelectContent>
                  </Select>
                  <Select value={notifReadyFilterReport} onValueChange={setNotifReadyFilterReport}>
                    <SelectTrigger className="w-28 h-7 text-xs dark:bg-slate-800 bg-slate-100"><SelectValue placeholder="Report" /></SelectTrigger>
                    <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="yes">Report Filed</SelectItem><SelectItem value="no">No Report</SelectItem></SelectContent>
                  </Select>
                  <Select value={notifReadyFilterType} onValueChange={setNotifReadyFilterType}>
                    <SelectTrigger className="w-28 h-7 text-xs dark:bg-slate-800 bg-slate-100"><SelectValue placeholder="Type" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      {[...new Set((selectedProfile?.ready_for_arrest || []).map(r => r.incident_type).filter(Boolean))].sort().map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <Select value={notifReadyFilterIdMethod} onValueChange={setNotifReadyFilterIdMethod}>
                    <SelectTrigger className="w-28 h-7 text-xs dark:bg-slate-800 bg-slate-100"><SelectValue placeholder="ID Method" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Methods</SelectItem>
                      {[...new Set((selectedProfile?.ready_for_arrest || []).flatMap(r => r.identification_methods || []))].sort().map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <ScrollArea className="h-[45vh]">
                {(selectedProfile?.ready_for_arrest || []).length === 0 ? <p className="text-slate-500 text-center py-8">No ready for arrest</p> : (
                  <div className="space-y-2 pr-2">
                    {(selectedProfile?.ready_for_arrest || [])
                      .filter(rfa => {
                        if (notifReadySearch && !(rfa.headline || '').toLowerCase().includes(notifReadySearch.toLowerCase()) && !(rfa.address || '').toLowerCase().includes(notifReadySearch.toLowerCase())) return false;
                        if (notifReadyFilterArrested === "yes" && !rfa.arrest_linked) return false;
                        if (notifReadyFilterArrested === "no" && rfa.arrest_linked) return false;
                        if (notifReadyFilterReport === "yes" && !rfa.report_filed) return false;
                        if (notifReadyFilterReport === "no" && rfa.report_filed) return false;
                        if (notifReadyFilterType !== "all" && rfa.incident_type !== notifReadyFilterType) return false;
                        if (notifReadyFilterIdMethod !== "all" && !(rfa.identification_methods || []).includes(notifReadyFilterIdMethod)) return false;
                        return true;
                      })
                      .sort((a, b) => notifReadySort === "newest" ? new Date(b.date_time || b.created_at || 0) - new Date(a.date_time || a.created_at || 0) : notifReadySort === "oldest" ? new Date(a.date_time || a.created_at || 0) - new Date(b.date_time || b.created_at || 0) : (a.incident_type || '').localeCompare(b.incident_type || ''))
                      .map((rfa, i) => (
                        <div
                          key={rfa.id || i}
                          onClick={() => prepareNotification("ready", rfa)}
                          className="p-3 rounded-lg cursor-pointer hover:ring-2 hover:ring-purple-500 border dark:bg-amber-900/10 bg-amber-50 dark:border-amber-700/30 border-amber-200"
                        >
                          <div className="flex items-center gap-1 mb-1 flex-wrap">
                            <Badge variant="outline" className="text-xs">{rfa.incident_type}</Badge>
                            {rfa.report_filed && <Badge className="bg-blue-500 text-white text-xs">REPORT FILED</Badge>}
                            {rfa.arrest_linked && <Badge className="bg-emerald-500 text-white text-xs">ARRESTED</Badge>}
                          </div>
                          <p className="font-medium dark:text-white text-slate-900">{rfa.headline}</p>
                          <p className="text-xs text-slate-500"><MapPin className="w-3 h-3 inline mr-1" />{rfa.address} • {formatShortDate(rfa.date_time)}</p>
                          {rfa.identification_methods?.length > 0 && (
                            <div className="flex gap-1 flex-wrap mt-1">{rfa.identification_methods.map(m => <Badge key={m} variant="outline" className="text-xs">{m}</Badge>)}</div>
                          )}
                          <p className="text-xs text-amber-400 mt-2">Click to send: "{selectedProfile?.name}" READY FOR ARREST in regards to "{rfa.headline}"</p>
                        </div>
                      ))}
                  </div>
                )}
              </ScrollArea>
            </TabsContent>

            {/* Arrests Tab */}
            <TabsContent value="arrests" className="m-0 mt-2">
              <div className="flex gap-2 mb-2">
                <Input placeholder="Search..." value={notifArrestsSearch} onChange={(e) => setNotifArrestsSearch(e.target.value)} className="h-8 text-xs flex-1 dark:bg-slate-800 bg-slate-100" />
                <Select value={notifArrestsSort} onValueChange={setNotifArrestsSort}>
                  <SelectTrigger className="w-24 h-8 text-xs dark:bg-slate-800 bg-slate-100"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="newest">Newest</SelectItem><SelectItem value="oldest">Oldest</SelectItem><SelectItem value="type">Type</SelectItem></SelectContent>
                </Select>
                <Select value={notifArrestsFilterHasReady} onValueChange={setNotifArrestsFilterHasReady}>
                  <SelectTrigger className="w-32 h-8 text-xs dark:bg-slate-800 bg-slate-100"><SelectValue placeholder="Linked Ready" /></SelectTrigger>
                  <SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="yes">Has Ready</SelectItem><SelectItem value="no">No Ready</SelectItem></SelectContent>
                </Select>
              </div>
              <ScrollArea className="h-[50vh]">
                {(selectedProfile?.arrests || []).length === 0 ? <p className="text-slate-500 text-center py-8">No arrests</p> : (
                  <div className="space-y-2 pr-2">
                    {(selectedProfile?.arrests || [])
                      .filter(arr => {
                        if (notifArrestsSearch && !(arr.headline || '').toLowerCase().includes(notifArrestsSearch.toLowerCase()) && !(arr.incident_type || '').toLowerCase().includes(notifArrestsSearch.toLowerCase()) && !(arr.address || '').toLowerCase().includes(notifArrestsSearch.toLowerCase())) return false;
                        if (notifArrestsFilterHasReady === "yes" && !arr.linked_ready_for_arrest_id && !arr.linked_ready_for_arrest_ids?.length) return false;
                        if (notifArrestsFilterHasReady === "no" && (arr.linked_ready_for_arrest_id || arr.linked_ready_for_arrest_ids?.length)) return false;
                        return true;
                      })
                      .sort((a, b) => notifArrestsSort === "newest" ? new Date(b.arrest_date || b.date_time || 0) - new Date(a.arrest_date || a.date_time || 0) : notifArrestsSort === "oldest" ? new Date(a.arrest_date || a.date_time || 0) - new Date(b.arrest_date || b.date_time || 0) : (a.incident_type || '').localeCompare(b.incident_type || ''))
                      .map((arr, i) => {
                        // Find linked ready for arrest record
                        const linkedReadyId = arr.linked_ready_for_arrest_id || arr.linked_ready_for_arrest_ids?.[0];
                        const linkedReady = linkedReadyId ? (selectedProfile?.ready_for_arrest || []).find(r => r.id === linkedReadyId || r.incident_id === linkedReadyId) : null;

                        return (
                          <div
                            key={arr.arrest_id || i}
                            className="p-3 dark:bg-emerald-900/20 bg-emerald-50 border dark:border-emerald-700/30 border-emerald-200 rounded-lg"
                          >
                            <div className="flex items-center gap-1 mb-1">
                              <Badge variant="outline" className="text-xs">{arr.incident_type}</Badge>
                              {linkedReady && <Badge className="bg-amber-500 text-white text-xs">FROM READY</Badge>}
                            </div>
                            <p className="font-medium dark:text-white text-slate-900">{arr.headline}</p>
                            <p className="text-xs text-slate-500"><MapPin className="w-3 h-3 inline mr-1" />{arr.address}</p>
                            <p className="text-xs text-emerald-600 mt-1"><Calendar className="w-3 h-3 inline mr-1" />Arrested: {formatShortDate(arr.arrest_date)}</p>

                            {/* Notification options */}
                            <div className="mt-3 pt-2 border-t dark:border-emerald-700/50 border-emerald-300 space-y-2">
                              {/* Always show arrest incident option */}
                              <div
                                onClick={() => prepareNotification("arrest", arr, null)}
                                className="p-2 rounded cursor-pointer hover:ring-2 hover:ring-purple-500 dark:bg-emerald-900/40 bg-emerald-100 border dark:border-emerald-600/50 border-emerald-300"
                              >
                                <p className="text-xs text-emerald-400">
                                  <span className="font-medium">Send with Arrest Incident:</span>
                                </p>
                                <p className="text-xs text-emerald-300 mt-1">
                                  "{selectedProfile?.name}" ARRESTED in connection with "{arr.headline}"
                                </p>
                              </div>

                              {/* Show linked ready option if available */}
                              {linkedReady && (
                                <div
                                  onClick={() => prepareNotification("arrest", arr, linkedReady.headline)}
                                  className="p-2 rounded cursor-pointer hover:ring-2 hover:ring-purple-500 dark:bg-amber-900/40 bg-amber-100 border dark:border-amber-600/50 border-amber-300"
                                >
                                  <p className="text-xs text-amber-400">
                                    <span className="font-medium">Send with Linked Ready:</span>
                                  </p>
                                  <p className="text-xs text-amber-300 mt-1">
                                    "{selectedProfile?.name}" ARRESTED in connection with "{linkedReady.headline}"
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}
              </ScrollArea>
            </TabsContent>
          </Tabs>

          {sendingNotif && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500"></div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Notification Confirmation Dialog */}
      <Dialog open={!!notifConfirm} onOpenChange={(open) => !open && setNotifConfirm(null)}>
        <DialogContent className="dark:bg-slate-900 bg-white dark:border-slate-800 border-slate-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 dark:text-white text-slate-900">
              <Bell className="w-5 h-5 text-purple-500" />
              Confirm Notification
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-sm text-slate-400 mb-2">Edit the notification message before sending to all users:</p>
            <Textarea
              value={notifConfirm?.message || ""}
              onChange={(e) => setNotifConfirm({ ...notifConfirm, message: e.target.value })}
              className="dark:bg-slate-800 bg-slate-100 dark:border-slate-700 border-slate-200 dark:text-white text-slate-900 font-medium min-h-[80px]"
            />
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setNotifConfirm(null)} disabled={sendingNotif}>Cancel</Button>
            <Button className="bg-purple-600 hover:bg-purple-700 text-white" onClick={confirmSendNotification} disabled={sendingNotif}>
              {sendingNotif ? "Sending..." : "Send Notification"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Mugshot Confirmation */}
      <Dialog open={showDeleteMugshotDialog} onOpenChange={setShowDeleteMugshotDialog}>
        <DialogContent className="dark:bg-slate-900 bg-white dark:border-slate-800 border-slate-200 max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-500">
              <Trash2 className="w-5 h-5" />
              Delete Mugshot
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500 py-2">Are you sure you want to delete the mugshot for <strong className="dark:text-white text-slate-900">{selectedProfile?.name}</strong>? This action cannot be undone.</p>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setShowDeleteMugshotDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteMugshot}>Delete</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete ID/License Confirmation */}
      <Dialog open={showDeleteIdLicenseDialog} onOpenChange={setShowDeleteIdLicenseDialog}>
        <DialogContent className="dark:bg-slate-900 bg-white dark:border-slate-800 border-slate-200 max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-500">
              <Trash2 className="w-5 h-5" />
              Delete ID/License
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500 py-2">Are you sure you want to delete the ID/License for <strong className="dark:text-white text-slate-900">{selectedProfile?.name}</strong>? This action cannot be undone.</p>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setShowDeleteIdLicenseDialog(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleDeleteIdLicense}>Delete</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Board Profile Preview Dialog */}
      <Dialog open={!!boardPreviewProfile} onOpenChange={(open) => { if (!open) setBoardPreviewProfile(null); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto overflow-x-hidden dark:bg-slate-900 bg-white dark:border-slate-800 border-slate-200 p-0">
          {boardPreviewLoading ? (
            <div className="flex items-center justify-center h-48"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-red-500" /></div>
          ) : boardPreviewProfile && (() => {
            const bp = boardPreviewProfile;
            const statusOpt = STATUS_OPTIONS.find(o => o.value === bp.status) || STATUS_OPTIONS[0];
            // Build lookup maps for ready and arrest records
            const bpReadyMap = {};
            (bp.ready_for_arrest || []).forEach(rfa => { if (rfa.incident_id) bpReadyMap[rfa.incident_id] = rfa; });
            const bpArrestMap = {};
            (bp.arrests || []).forEach(arr => { if (arr.incident_id) bpArrestMap[arr.incident_id] = arr; });
            const bpReadyIdToArrest = {};
            (bp.arrests || []).forEach(arr => {
              const ids = arr.linked_ready_for_arrest_ids || (arr.linked_ready_for_arrest_id ? [arr.linked_ready_for_arrest_id] : []);
              ids.forEach(rfaId => { bpReadyIdToArrest[rfaId] = arr; });
            });

            const allIncidents = [];
            const seenIds = new Set();
            (bp.linked_incidents_details || []).forEach(inc => {
              if (!seenIds.has(inc.incident_id)) {
                seenIds.add(inc.incident_id);
                const rfa = bpReadyMap[inc.incident_id];
                const arr = bpArrestMap[inc.incident_id];
                const linkedArr = rfa ? (bpReadyIdToArrest[rfa.id] || bpReadyIdToArrest[rfa.incident_id]) : null;
                allIncidents.push({ ...inc, source: "linked", hasReady: !!rfa, hasArrest: !!arr, arrestDate: arr?.arrest_date, linkedArrestIncidentId: (linkedArr && linkedArr.incident_id !== inc.incident_id) ? linkedArr.incident_id : null, linkedArrestHeadline: linkedArr?.headline });
              }
            });
            (bp.ready_for_arrest || []).forEach(rfa => {
              if (rfa.incident_id && !seenIds.has(rfa.incident_id)) {
                seenIds.add(rfa.incident_id);
                const arr = bpArrestMap[rfa.incident_id];
                const linkedArr = bpReadyIdToArrest[rfa.id] || bpReadyIdToArrest[rfa.incident_id];
                allIncidents.push({ incident_id: rfa.incident_id, headline: rfa.headline, incident_type: rfa.incident_type, address: rfa.address, date_time: rfa.date_time, source: "ready", hasReady: true, hasArrest: !!arr, arrestDate: arr?.arrest_date, linkedArrestIncidentId: (linkedArr && linkedArr.incident_id !== rfa.incident_id) ? linkedArr.incident_id : null, linkedArrestHeadline: linkedArr?.headline });
              }
            });
            (bp.arrests || []).forEach(arr => {
              if (arr.incident_id && !seenIds.has(arr.incident_id)) {
                seenIds.add(arr.incident_id);
                allIncidents.push({ incident_id: arr.incident_id, headline: arr.headline, incident_type: arr.incident_type, address: arr.address, date_time: arr.date_time, source: "arrest", hasReady: false, hasArrest: true, arrestDate: arr.arrest_date });
              }
            });
            return (
              <div>
                {/* Header with photos */}
                <div className="p-4 pb-3 border-b dark:border-slate-800 border-slate-200">
                  <div className="flex gap-3 mb-3">
                    {bp.mugshot_url ? (
                      <div
                        className="relative w-20 h-20 group cursor-pointer flex-shrink-0"
                        onClick={() => { setLightboxImage(getThumbUrl(bp.mugshot_url, 1600)); setLightboxMediaType('image'); setLightboxHideLogo(false); }}
                      >
                        <img src={getThumbUrl(bp.mugshot_url, 400)} alt="" className="w-20 h-20 object-cover rounded-lg" />
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center">
                          <span className="text-white text-xs">Click to enlarge</span>
                        </div>
                        {user?.role !== "admin" && (
                          <div className="absolute inset-0 pointer-events-none flex items-center justify-center overflow-hidden rounded-lg">
                            <div className="transform -rotate-45 text-center select-none">
                              <p className="text-[4px] text-white/40 whitespace-nowrap">{user?.name || "User"}</p>
                              <p className="text-[5px] font-bold text-white/35 whitespace-nowrap">INTERNAL</p>
                            </div>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="w-20 h-20 dark:bg-slate-800 bg-slate-200 rounded-lg flex items-center justify-center flex-shrink-0"><UserCircle className="w-10 h-10 text-slate-400" /></div>
                    )}
                    <div className="min-w-0 flex-1 overflow-hidden pr-6">
                      <h2 className="text-lg font-bold dark:text-white text-slate-900 truncate">{bp.name || "Unknown"}</h2>
                      <Badge className={`${statusOpt.color} text-white text-xs mt-1`}>{statusOpt.label}</Badge>
                      {bp.dob && <p className="text-xs text-slate-400 mt-1">DOB: {formatDob(bp.dob)}</p>}
                      {bp.known_address && <p className="text-xs dark:text-slate-400 text-slate-500 mt-1 truncate"><MapPin className="w-3 h-3 inline mr-1" />{bp.known_address}</p>}
                      <p className="text-[10px] text-slate-500 font-mono mt-1">{bp.profile_id}</p>
                      {(bp.incident_types || []).length > 0 && <div className="mt-2">{renderOffenseBadges(bp, "compact")}</div>}
                    </div>
                  </div>
                  {bp.id_license_url && (
                    <div className="flex items-center gap-2 mb-2">
                      <div
                        className="relative w-16 h-16 group cursor-pointer"
                        onClick={() => { setLightboxImage(getThumbUrl(bp.id_license_url, 1600)); setLightboxMediaType('image'); setLightboxHideLogo(true); }}
                      >
                        <img src={getThumbUrl(bp.id_license_url, 200)} alt="ID" className="w-16 h-16 object-cover rounded" />
                        <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded flex items-center justify-center">
                          <span className="text-white text-[9px]">Enlarge</span>
                        </div>
                      </div>
                      <span className="text-[10px] text-slate-500">ID/License</span>
                    </div>
                  )}
                </div>
                {/* Stats */}
                <div className="grid grid-cols-3 gap-3 p-4 pb-2">
                  <div className="dark:bg-slate-800 bg-slate-100 p-2 rounded-lg text-center"><p className="text-xl font-bold dark:text-white text-slate-900">{allIncidents.length}</p><p className="text-[10px] text-slate-500 uppercase">Linked</p></div>
                  <div className="dark:bg-amber-900/30 bg-amber-50 p-2 rounded-lg text-center border dark:border-amber-700/30 border-amber-200"><p className="text-xl font-bold text-amber-600">{(bp.ready_for_arrest || []).length}</p><p className="text-[10px] text-amber-600 uppercase">Ready</p></div>
                  <div className="dark:bg-emerald-900/30 bg-emerald-50 p-2 rounded-lg text-center border dark:border-emerald-700/30 border-emerald-200"><p className="text-xl font-bold text-emerald-600">{(bp.arrests || []).length}</p><p className="text-[10px] text-emerald-600 uppercase">Arrests</p></div>
                </div>
                {/* Info */}
                {(bp.affiliates || bp.notes) && (
                  <div className="px-4 pb-2 space-y-2">
                    {bp.affiliates && <div className="p-2 dark:bg-slate-800/50 bg-slate-50 rounded-lg overflow-hidden"><p className="text-[10px] text-slate-500 uppercase mb-1"><Users className="w-3 h-3 inline mr-1" />Affiliates</p><p className="text-sm dark:text-white text-slate-900 break-words">{bp.affiliates}</p></div>}
                    {bp.notes && <div className="p-2 dark:bg-slate-800/50 bg-slate-50 rounded-lg overflow-hidden"><p className="text-[10px] text-slate-500 uppercase mb-1"><FileText className="w-3 h-3 inline mr-1" />Notes</p><p className="text-sm dark:text-white text-slate-900 whitespace-pre-wrap break-words">{bp.notes}</p></div>}
                  </div>
                )}
                {/* Linked Incidents */}
                {allIncidents.length > 0 && (
                  <div className="px-4 pb-4">
                    <p className="text-xs text-slate-500 uppercase mb-2">Linked Incidents</p>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {allIncidents.sort((a, b) => new Date(b.date_time || 0) - new Date(a.date_time || 0)).map((inc, i) => (
                        <div key={inc.incident_id || i} onClick={() => { if (hasIncidentsAccess) { setBoardPreviewProfile(null); navigate(`/incidents/${inc.incident_id}`); } }} className={`p-2 rounded border-l-[3px] overflow-hidden ${inc.hasArrest ? "border-l-emerald-500 dark:bg-emerald-900/15 bg-emerald-50" : inc.hasReady ? "border-l-amber-500 dark:bg-amber-900/15 bg-amber-50" : "border-l-blue-500 dark:bg-blue-900/15 bg-blue-50"} border dark:border-slate-700/50 border-slate-200 ${hasIncidentsAccess ? "cursor-pointer dark:hover:bg-slate-800 hover:bg-slate-100" : ""}`}>
                          <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                            <Badge variant="outline" className="text-[10px]">{inc.incident_type}</Badge>
                            {inc.hasArrest && <Badge className="bg-emerald-500 text-white text-[10px]">Arrested</Badge>}
                            {inc.hasReady && <Badge className="bg-amber-500 text-white text-[10px]">Ready</Badge>}
                            {inc.linkedArrestIncidentId && <Badge className="bg-emerald-700 text-white text-[10px] cursor-pointer hover:bg-emerald-600" onClick={(e) => { e.stopPropagation(); setBoardPreviewProfile(null); navigate(`/incidents/${inc.linkedArrestIncidentId}`); }}>Arrest Via ↗</Badge>}
                            {!inc.hasReady && !inc.hasArrest && !inc.linkedArrestIncidentId && <Badge className="bg-blue-500 text-white text-[10px]">Linked</Badge>}
                          </div>
                          <p className="text-sm font-medium dark:text-white text-slate-900 break-words">{inc.headline || inc.incident_id}</p>
                          <p className="text-[10px] text-slate-500 break-words">{inc.address} • {formatShortDate(inc.date_time)}</p>
                          {inc.hasArrest && inc.arrestDate && <p className="text-[10px] text-emerald-500 mt-0.5">Arrested: {formatShortDate(inc.arrestDate)}</p>}
                          {inc.linkedArrestIncidentId && (
                            <p className="text-[10px] text-emerald-400 mt-0.5 cursor-pointer hover:underline" onClick={(e) => { e.stopPropagation(); setBoardPreviewProfile(null); navigate(`/incidents/${inc.linkedArrestIncidentId}`); }}>
                              {inc.linkedArrestHeadline || inc.linkedArrestIncidentId} →
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* Open Full Profile button */}
                <div className="px-4 pb-4">
                  <Button className="w-full bg-red-700 hover:bg-red-600 text-white" onClick={() => { setBoardPreviewProfile(null); setActiveTab("profiles"); fetchProfileDetails(bp.profile_id); }}>
                    Open Full Profile
                  </Button>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {lightboxImage && (
        <ImageLightbox
          src={lightboxImage}
          alt={selectedProfile?.name || "Profile image"}
          mediaType={lightboxMediaType}
          onClose={() => { setLightboxImage(null); setLightboxMediaType(null); setLightboxHideLogo(false); }}
          showDownload={true}
          hideLogo={lightboxHideLogo}
        />
      )}
      {/* Incident navigation confirmation */}
      <AlertDialog open={!!pendingIncidentNav} onOpenChange={(open) => { if (!open) setPendingIncidentNav(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Open Incident</AlertDialogTitle>
            <AlertDialogDescription>
              You are about to leave this page and open the incident. Continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmIncidentNav}>Continue</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
